import { clsx } from '@alexis/helpers/clsx';
import { getCookie } from '@alexis/helpers/cookie';
import { getLocalDate, numberOfNights, toISOStringWithTimezone } from '@alexis/helpers/date';
import { isDevelopmentEnvironment, isStagingEnvironment } from '@alexis/helpers/environment';
import { genzoTrack, genzoTrackActionEvent, genzoTrackPageView } from '@alexis/helpers/genzo';
import { gtmTrack } from '@alexis/helpers/gtm';
import { generateCUID } from '@alexis/helpers/identity';
import sha256 from 'crypto-js/sha256';
import { clamp } from 'lodash-es';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AutoSizer, List, type ListRowProps, type Size, WindowScroller } from 'react-virtualized';
import type { Index, WindowScrollerChildProps } from 'react-virtualized';
import type { SortOrder } from 'sortOrder';

import { translateText } from '@wego/alexis/helpers/translation';

import AdsBlockerBait from '@components/AdsBlockerBait';
import AlertMessage, { MessageTypes } from '@components/AlertMessage/AlertMessage';
import RefreshPopup from '@components/RefreshPopup';
import { ProductTypes } from '@components/RefreshPopup/RefreshPopup';

import { GTM_ID } from '@constants/general';
import {
  ADDING_TRAVELER_TYPE_BOX,
  DISPLAY_3_LATEST_REVIEWS,
  NEW_RATING_SCALE,
} from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import { convertListToHashMap, notUndefinedAndNull } from '@helpers/array';
import { impressionLogHotelSearchResult, impressionLogHotelCardClick } from '@helpers/genzo';
import {
  convertGuestRoomsToSearchParam,
  convertGuestSearchParamToGuestRooms,
  filterHotelViewModelBasedOnFilters,
  getHotelSortType,
  getHotelSortTypeEventValue,
  getHotelSortTypeSearchParamValue,
  sortHotelViewModelBasedOnDiscount,
  sortHotelViewModelBasedOnDistance,
  sortHotelViewModelBasedOnPrice,
  sortHotelViewModelBasedOnReviewScore,
  sortHotelViewModelBasedOnSaving,
  sortHotelViewModelBasedOnScore,
  sortHotelViewModelBasedOnStar,
} from '@helpers/hotel';
import { convertHotelRatePriceToPrice } from '@helpers/price';
import { hotelsRoute } from '@helpers/routeTranslation';
import { findClosest, isValidDate } from '@helpers/utils';
import {
  webEngageTrackHotelSearch,
  webEngageTrackRateSearchResult,
  webEngageTrackPurchase,
} from '@helpers/webEngage';

import AdjustmentIcon from '@icons/adjustments.svg';
import CloseFilledIcon from '@icons/circle_filled_cross.svg';
import CloseIcon from '@icons/close.svg';
import DoubleArrowRightIcon from '@icons/double_arrow_right.svg';
import ListViewIcon from '@icons/list_view.svg';
import MapIcon from '@icons/map.svg';
import MegaPhoneIcon from '@icons/mega-phone.svg';
import SearchIcon from '@icons/search.svg';

import { useBodyScrollable } from '@hooks/useBodyScrollable';
import { useClarityCustomTag } from '@hooks/useClarity';
import { useFbpFbc } from '@hooks/useFbpFbc';
import { useGPTAdsInitialization } from '@hooks/useGPTAdsInitialization';
import { useGPTAdsRefresh } from '@hooks/useGPTAdsRefresh';
import { useIsOnline } from '@hooks/useOnline';
import { useViewportSize } from '@hooks/useViewportSize';

import { hotelSearchObservable } from '@observables/hotelObservables';
import { setHotelHovered } from '@observables/mapObservables';

import { hotelSearchResultObserver } from '@observers/hotelObserver';

import { resetHotelMetaSearch } from '@redux/actions/hotelMetaSearchActions';
import { changeHomePageViewId } from '@redux/actions/pageViewIdActions';
import {
  changeIsIncludingTaxOrFee,
  changeShowTotalPrice,
} from '@redux/actions/priceOptionsActions';
import { updateTriggerSearchCounter } from '@redux/actions/triggerSearchCounterActions';
import {
  getCurrencyState,
  getCurrentSiteState,
  getExchangeRateState,
  getHotelMetaSearchState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
  getTriggerSearchCounterState,
  getUserState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import { CurrentSite } from '@wegoTypes/currentSite';
import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { HotelSortType } from '@wegoTypes/hotels/hotelSortType';
import { HotelViewModel } from '@wegoTypes/hotels/hotelViewModel';
import { HotelLatestReviews } from '@wegoTypes/hotels/metasearch/hotelLatestReviews';
import { RateViewModel } from '@wegoTypes/hotels/rateViewModel';
import {
  HotelSearchResultGtmPageData,
  HotelGtmSkycannerTopDeal,
} from '@wegoTypes/tracking/gtm/hotel';
import { Translations } from '@wegoTypes/translations';
import { User } from '@wegoTypes/user';

import HotelCard from './components/HotelCard';
import HotelCardSkeleton from './components/HotelCardSkeleton';
import HotelFilter from './components/HotelFilter/HotelFilter';
import HotelLoginBanner from './components/HotelLoginBanner';
import HotelMap from './components/HotelMap/HotelMap';
import HotelSort from './components/HotelSort/HotelSort';
import TravelerTypeBox from './components/TravelerTypeBox';
import { useGetHotelLatestReviews } from './hooks/useGetLatestReviews';
import { useGetHotelPublicPaymentOption } from './hooks/useGetPublicPaymentOption';
import styles from './HotelSearchResult.module.scss';

interface HotelSearchResultProps {
  gtmAuth: string;
  gtmPreview: string;
  localeParam: string | undefined;
  nearestCityByIP: Place | undefined;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
}

const HotelSearchResult: React.FC<HotelSearchResultProps> = ({
  gtmAuth,
  gtmPreview,
  localeParam,
  nearestCityByIP,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}): JSX.Element => {
  let hoverTimer: NodeJS.Timeout;
  const { webEngageAnonymousId, userCentricCoreConfigs } = useContext(GlobalContext);
  const apiBaseUrl: string = API_BASE_URL;
  const currency: Currency = useSelector(getCurrencyState);
  const currentSite: CurrentSite = useSelector(getCurrentSiteState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const isRtl: boolean = useSelector(getIsRtlState);
  const locale: string = useSelector(getLocaleState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations: Translations = useSelector(getTranslationsState);
  const triggerSearchCounter: number = useSelector(getTriggerSearchCounterState);
  const user: User | null = useSelector(getUserState);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, setIsBodyScrollable] = useBodyScrollable();
  const { width } = useViewportSize();
  const { fbp, fbc } = useFbpFbc();
  const { isOnline } = useIsOnline();

  const { pathname, search, state } = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams<{
    cityCode?: string;
    districtId?: string;
    regionId?: string;
    checkInDate: string;
    checkOutDate: string;
  }>();

  const locationState = useLocation().state as {
    impressionType: 'sort' | 'filter' | 'page_load' | 'map_load';
    eventTrackingObj?: Record<string, any>;
  };

  const isNewRatingScale =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === NEW_RATING_SCALE)?.value === '1';

  const isShowTravelerBox =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === ADDING_TRAVELER_TYPE_BOX)
      ?.value === '1';
  const isDisplayLatestReviews =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === DISPLAY_3_LATEST_REVIEWS)
      ?.value === '1';

  useClarityCustomTag(ADDING_TRAVELER_TYPE_BOX, isShowTravelerBox ? 'variantA' : 'baseline');

  const { data: hotelsLatestReviews } = useGetHotelLatestReviews(
    {
      cityCode: params.cityCode!,
    },
    {
      enabled: !!params.cityCode && isDisplayLatestReviews,
      refetchOnWindowFocus: false,
    },
  );

  // Get public payment options
  const { data: paymentOptions } = useGetHotelPublicPaymentOption(
    {
      clientCode: 'bow_hot',
    },
    {
      enabled: !!currentSite.countryCode && !!currency.code,
      refetchOnWindowFocus: false,
    },
  );

  const listRef = useRef<List>(null);
  const popularFiltersRef = useRef<string[] | null>(null);
  const eventIdRef = useRef<string | null>(null);
  const searchboxRef = useRef<HTMLInputElement | null>(null);

  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState<boolean>(false);
  const [isWebEngageTracked, setIsWebEngageTracked] = useState<boolean>(true);
  const [isGenzoTracked, setIsGenzoTracked] = useState<boolean>(true);
  const [isGenzoVisitsTracked, setIsGenzoVisitsTracked] = useState<boolean>(true);
  const [isGtmTracked, setIsGtmTracked] = useState<boolean>(true);
  const [hotelMetaSearchHasError, setHotelMetaSearchHasError] = useState<boolean>(false);
  const [hotelMetaSearchError, setHotelMetaSearchError] = useState<{
    message: string;
    status: string;
  }>({
    message: '',
    status: '',
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMapDelayed, setIsLoadingMapDelayed] = useState(true);

  const [isSearchExpired, setIsSearchExpired] = useState<boolean>(false);

  const [windowInnerWidth, setWindowInnerWidth] = useState<number>(0);

  const [googleSlots, setGoogleSlots] = useState<Array<googletag.Slot>>();
  const [topLeaderboardAdsHeight, setTopLeaderboardAdsHeight] = useState<number>(0);
  const [middleLeaderboardAdsHeight, setMiddleLeaderboardAdsHeight] = useState<number>(0);
  const [bottomLeaderboardAdsHeight, setBottomLeaderboardAdsHeight] = useState<number>(0);
  const [isNativeTopAdRendered, setIsNativeTopAdRendered] = useState<boolean>(false);
  const [isNativeMiddleAdRendered, setIsNativeMiddleAdRendered] = useState<boolean>(false);
  const [isNativeBottomAdRendered, setIsNativeBottomAdRendered] = useState<boolean>(false);
  const [isNewHotelInTownAdRendered, setIsNewHotelInTownAdRendered] = useState<boolean>(false);

  const [nativeTopAdHeight, setNativeTopAdHeight] = useState<number>(0);
  const [nativeMiddleAdHeight, setNativeMiddleAdHeight] = useState<number>(0);
  const [nativeBottomAdHeight, setNativeBottomAdHeight] = useState<number>(0);
  const [newHotelInTownAdHeight, setNewHotelInTownAdHeight] = useState<number>(0);

  const [hasMrecOrStickyAds, setHasMrecOrStickyAds] = useState<boolean>(false);
  const [showStickyBottom, setShowStickyBottom] = useState<boolean>(false);

  const [googleMap, setGoogleMap] = useState<google.maps.Map>();
  const [isMapClosed, setIsMapClosed] = useState(false);
  const [listRenderId, setListRenderId] = useState<{ [key: number]: boolean }>({});
  const [isOpenSearchbox, setIsOpenSearchbox] = useState(false);
  const [isMapFullscreen, setMapFullscreen] = useState(false);
  const [isOpenTravelerTypeBox, setIsOpenTravelerTypeBox] = useState(false);

  const guestsSearchParam = searchParams.get('guests');
  const sortTypeSearchParam = searchParams.get('sort');
  const sortOrderSearchParam = searchParams.get('order');

  const amenityIdsSearchParam = searchParams.get('amenities');
  const bookingOptionsSearchParam = searchParams.get('providers');
  const brandIdsSearchParam = searchParams.get('brands');
  const chainIdsSearchParam = searchParams.get('chains');
  const districtIdsSearchParam = searchParams.get('districts');
  const isTodayDealsSearchParam = searchParams.get('deals');
  const nameSearchParam = searchParams.get('name');
  const pricesSearchParam = searchParams.get('price');
  const propertyTypeIdsSearchParam = searchParams.get('accommodation_types');
  const rateAmenityIdsSearchParam = searchParams.get('free_amenities');
  const reviewerGroupsSearchParam = searchParams.get('guest_rating');
  const reviewScoreSearchParam = searchParams.get('guest_score');
  const selectedHotelIdsSearchParam = searchParams.get('selected_hotel_ids');
  const starIdsSearchParam = searchParams.get('stars');
  const providerCodesSearchParam = searchParams.get('provider_codes');

  const wegoInternalCampaignSearchParam = searchParams.get('wg_internal_campaign');
  const isCrossSell = wegoInternalCampaignSearchParam === 'hotels_crosssell';

  const bowOnlySearchParam = searchParams.get('bow_only');
  const tsCodeSearchParam = searchParams.get('ts_code');

  const wegoSourceSearchParam = searchParams.get('wg_source');
  const wegoCampaignSearchParam = searchParams.get('wg_campaign');
  const wegoMediumSearchParam = searchParams.get('wg_medium');
  const radiusLatParam = searchParams.get('lat');
  const radiusLongParam = searchParams.get('long');
  const radiusParam = searchParams.get('radius');

  const viewSearchParam = searchParams.get('view');

  // Google hotel
  const wegoHotelIdSearchParam = searchParams.get('wego_hotel_id');
  const wegoPriceSearchParam = searchParams.get('wego_price');
  const providerSearchParam = searchParams.get('provider');
  const wegoGrowthSearchParam = searchParams.get('wego_growth');
  const ghSearchParam = searchParams.get('gh_params');

  const isMapVisible = !!viewSearchParam && viewSearchParam === 'map' ? true : false;
  const isHalfScreenMap = width > 1208;
  const isScreenBetween1200And1340 = windowInnerWidth > 1200 && windowInnerWidth <= 1340;
  const isScreenAbove1340 = windowInnerWidth > 1340;
  const hasFilteredApplied = [
    'name',
    'deals',
    'amenities',
    'providers',
    'brands',
    'chains',
    'districts',
    'price',
    'accomodation_types',
    'free_amenities',
    'guest_rating',
    'guest_score',
    'stars',
    'provider_codes',
  ].some((item) => searchParams.has(item));
  const isLoadingSideMap =
    isMapVisible && (isLoadingMapDelayed || (isLoading && !hotelMetaSearch.hotelViewModels.length));

  const pageViewId = useMemo<string>(() => {
    return generateCUID();
  }, []);

  const isDatesValid = useMemo<boolean>(() => {
    if (!params.checkInDate || !params.checkOutDate) return false;
    return (
      isValidDate(params.checkInDate, true) &&
      isValidDate(params.checkOutDate) &&
      new Date(params.checkInDate) < new Date(params.checkOutDate)
    );
  }, [params.checkInDate, params.checkOutDate]);

  const hasTabbyGateway = useMemo<boolean>(() => {
    return !!paymentOptions?.find((paymentOption) => paymentOption.channel === 'BNPL');
  }, [paymentOptions]);

  const hasEgyptCentralBankNotice = currentSite.countryCode === 'EG' ? true : false;

  //#region Filters
  const sortType = useMemo<HotelSortType>(() => {
    if (!!sortTypeSearchParam) {
      return getHotelSortType(sortTypeSearchParam);
    }
    return HotelSortType.Recommended;
  }, [sortTypeSearchParam]);

  const sortOrder = useMemo<SortOrder>(() => {
    if (!!sortOrderSearchParam && /^(?:asc|desc)$/i.test(sortOrderSearchParam)) {
      return sortOrderSearchParam.toLowerCase() === 'asc' ? 'asc' : 'desc';
    }
    return 'desc';
  }, [sortOrderSearchParam]);

  const todayDealsCount = useMemo<number>(() => {
    return hotelMetaSearch.hotelViewModels.filter(
      (hotelViewModel) => hotelViewModel.rates.length > 0 && !!hotelViewModel.rates[0].usualPrice,
    ).length;
  }, [hotelMetaSearch.hotelViewModels]);

  const getUsdPrices = useCallback(
    (isMinPrice: boolean) => {
      return hotelMetaSearch.hotelViewModels
        .map((hotelViewModel) => {
          const rate = hotelViewModel.rates[isMinPrice ? 0 : hotelViewModel.rates.length - 1];

          if (!!rate) {
            const { taxInclusive, amountUsd, totalAmountUsd, taxAmountUsd, totalTaxAmountUsd } =
              rate.price;
            let calculatedAmountUsd: number = 0;

            let toBeCalculatedAmountUsd = taxInclusive ? amountUsd - taxAmountUsd : amountUsd;
            let toBeCalculatedTaxAmountUsd = taxAmountUsd;

            if (priceOptions.showTotalPrice) {
              toBeCalculatedAmountUsd = taxInclusive
                ? totalAmountUsd - totalTaxAmountUsd
                : totalAmountUsd;
              toBeCalculatedTaxAmountUsd = totalTaxAmountUsd;
            }

            if (priceOptions.isIncludingTaxOrFee) {
              calculatedAmountUsd = toBeCalculatedAmountUsd + toBeCalculatedTaxAmountUsd;
            } else {
              calculatedAmountUsd = toBeCalculatedAmountUsd;
            }
            return calculatedAmountUsd;
          }
        })
        .filter(notUndefinedAndNull);
    },
    [hotelMetaSearch.hotelViewModels, priceOptions],
  );

  const minUsdPrice = Math.floor(Math.min(...getUsdPrices(true)));
  const maxUsdPrice = Math.ceil(Math.max(...getUsdPrices(false)));

  const selectedAmenityIds = useMemo<Array<number>>(() => {
    if (!!amenityIdsSearchParam) {
      return amenityIdsSearchParam.split(',').map((amenityId) => parseInt(amenityId));
    }
    return [];
  }, [amenityIdsSearchParam]);

  const selectedBookingOptions = useMemo<Array<string>>(() => {
    if (!!bookingOptionsSearchParam) {
      return bookingOptionsSearchParam.split(',');
    }
    return [];
  }, [bookingOptionsSearchParam]);

  const selectedBrandIds = useMemo<Array<number>>(() => {
    if (!!brandIdsSearchParam) {
      return brandIdsSearchParam.split(',').map((brandId) => parseInt(brandId));
    }
    return [];
  }, [brandIdsSearchParam]);

  const selectedChainIds = useMemo<Array<number>>(() => {
    if (!!chainIdsSearchParam) {
      return chainIdsSearchParam.split(',').map((chainId) => parseInt(chainId));
    }
    return [];
  }, [chainIdsSearchParam]);

  const selectedDistrictIds = useMemo<Array<number>>(() => {
    if (!!districtIdsSearchParam) {
      return districtIdsSearchParam.split(',').map((districtId) => parseInt(districtId));
    }
    return [];
  }, [districtIdsSearchParam]);

  const selectedUsdPrices = useMemo<Array<number>>(() => {
    if (!!pricesSearchParam) {
      return pricesSearchParam.split(',').map((price) => parseInt(price));
    } else {
      if (!!minUsdPrice && !!maxUsdPrice) {
        return [minUsdPrice, maxUsdPrice];
      }
      return [];
    }
  }, [minUsdPrice, maxUsdPrice, pricesSearchParam]);

  const selectedHotelIds = useMemo<Array<number>>(() => {
    if (!!selectedHotelIdsSearchParam) {
      return selectedHotelIdsSearchParam
        .split(',')
        .map((hotelId) => {
          if (!!hotelId) {
            return parseInt(hotelId);
          }
          return undefined;
        })
        .filter(notUndefinedAndNull);
    }
    return [];
  }, [selectedHotelIdsSearchParam]);

  const selectedPropertyTypeIds = useMemo<Array<number>>(() => {
    if (!!propertyTypeIdsSearchParam) {
      return propertyTypeIdsSearchParam
        .split(',')
        .map((propertyTypeId) => parseInt(propertyTypeId));
    }
    return [];
  }, [propertyTypeIdsSearchParam]);

  const selectedRateAmenityIds = useMemo<Array<number>>(() => {
    if (!!rateAmenityIdsSearchParam) {
      return rateAmenityIdsSearchParam.split(',').map((rateAmenityId) => parseInt(rateAmenityId));
    }
    return [];
  }, [rateAmenityIdsSearchParam]);

  const selectedReviewerGroups = useMemo<Array<string>>(() => {
    if (!!reviewerGroupsSearchParam) {
      return reviewerGroupsSearchParam.toUpperCase().split(',');
    }
    return [];
  }, [reviewerGroupsSearchParam]);

  const selectedRateProviderCodes = useMemo<Array<string>>(() => {
    if (!!providerCodesSearchParam) {
      return providerCodesSearchParam.split(',');
    }
    return [];
  }, [providerCodesSearchParam]);

  const reviewScoresRange = isNewRatingScale
    ? [-1, 51, 61, 71, 81, 91, 101]
    : [-1, 68, 75, 80, 86, 101];
  const selectedReviewScores = useMemo<Array<number>>(() => {
    if (!!reviewScoreSearchParam) {
      return reviewScoreSearchParam
        .split(',')
        .map(
          (reviewScore) =>
            reviewScoresRange[findClosest(reviewScoresRange, parseInt(reviewScore)) ?? 0],
        )
        .sort((a, b) => a - b);
    }
    return [reviewScoresRange[0], reviewScoresRange[reviewScoresRange.length - 1]];
  }, [reviewScoreSearchParam]);

  const selectedStarIds = useMemo<Array<number>>(() => {
    if (!!starIdsSearchParam) {
      return starIdsSearchParam.split(',').map((starId) => parseInt(starId));
    }
    return [];
  }, [starIdsSearchParam]);

  const sortHotelViewModels = useCallback<
    (
      hotelViewModels: Array<HotelViewModel>,
      hotelSortType: HotelSortType,
      sortOrder: SortOrder,
      priceOptions: PriceOptions,
    ) => Array<HotelViewModel>
  >(
    (
      hotelViewModels: Array<HotelViewModel>,
      hotelSortType: HotelSortType,
      sortOrder: SortOrder,
      priceOptions: PriceOptions,
    ) => {
      switch (hotelSortType) {
        case HotelSortType.Recommended:
          return sortHotelViewModelBasedOnScore(hotelViewModels);
        case HotelSortType.Price:
          return sortHotelViewModelBasedOnPrice(
            hotelViewModels,
            sortOrder,
            priceOptions.isIncludingTaxOrFee,
          );
        case HotelSortType.Discount:
          return sortHotelViewModelBasedOnDiscount(hotelViewModels);
        case HotelSortType.Saving:
          return sortHotelViewModelBasedOnSaving(hotelViewModels);
        case HotelSortType.AllReviewScore:
          return sortHotelViewModelBasedOnReviewScore(hotelViewModels, 'ALL');
        case HotelSortType.SoloReviewScore:
          return sortHotelViewModelBasedOnReviewScore(hotelViewModels, 'SOLO');
        case HotelSortType.BusinessReviewScore:
          return sortHotelViewModelBasedOnReviewScore(hotelViewModels, 'BUSINESS');
        case HotelSortType.FamilyReviewScore:
          return sortHotelViewModelBasedOnReviewScore(hotelViewModels, 'FAMILY');
        case HotelSortType.CoupleReviewScore:
          return sortHotelViewModelBasedOnReviewScore(hotelViewModels, 'COUPLE');
        case HotelSortType.NearestToCityCentre:
          return sortHotelViewModelBasedOnDistance(hotelViewModels, 'cityCentre');
        case HotelSortType.NearestToAirport:
          return sortHotelViewModelBasedOnDistance(hotelViewModels, 'airport');
        case HotelSortType.NearestToTargetLocation:
          return sortHotelViewModelBasedOnDistance(hotelViewModels, 'searchLocation');
        case HotelSortType.Star:
          return sortHotelViewModelBasedOnStar(hotelViewModels, sortOrder);
        default:
          return hotelViewModels;
      }
    },
    [],
  );

  const selectedHotelViewModels = useMemo<Array<HotelViewModel>>(() => {
    if (selectedHotelIds.length > 0) {
      const sortedHotelViewModels = sortHotelViewModels(
        hotelMetaSearch.hotelViewModels,
        sortType,
        sortOrder,
        priceOptions,
      );
      return sortedHotelViewModels.filter((hotelViewModel) =>
        selectedHotelIds.some((selectedHotelId) => selectedHotelId === hotelViewModel.hotelId),
      );
    }
    return [];
  }, [hotelMetaSearch.hotelViewModels, selectedHotelIds, sortType, sortOrder, priceOptions]);

  const googleHotelViewModels = useMemo<Array<HotelViewModel>>(() => {
    if (
      !!wegoHotelIdSearchParam &&
      !!providerSearchParam &&
      hotelMetaSearch.hotelViewModels.length !== 0
    ) {
      return hotelMetaSearch.hotelViewModels
        .filter((hotelViewModel) => +wegoHotelIdSearchParam === hotelViewModel.hotelId)
        .map((hotel) => {
          const pinnedRate = hotel.filteredRates.find(
            (rate) => rate.provider.code === window.atob(providerSearchParam),
          );
          return {
            ...hotel,
            filteredRates: [
              pinnedRate,
              ...hotel.filteredRates.filter((rate) => rate.id !== pinnedRate?.id),
            ].filter(notUndefinedAndNull),
          };
        });
    }
    return [];
  }, [
    hotelMetaSearch.hotelViewModels,
    sortType,
    sortOrder,
    priceOptions,
    wegoHotelIdSearchParam,
    providerSearchParam,
  ]);

  const sortedAndFilteredHotelViewModels = useMemo<Array<HotelViewModel>>(() => {
    let hotelViewModels = hotelMetaSearch.hotelViewModels;

    if (!!wegoHotelIdSearchParam && !!providerSearchParam) {
      hotelViewModels = hotelViewModels.filter(
        (hotelViewModel) => hotelViewModel.hotelId.toString() !== wegoHotelIdSearchParam,
      );
    }

    if (selectedHotelIds.length > 0) {
      hotelViewModels = hotelViewModels.filter(
        (hotelViewModel) =>
          !selectedHotelIds.some((selectedHotelId) => selectedHotelId === hotelViewModel.hotelId),
      );
    }

    let filteredHotelViewModels: Array<HotelViewModel> = filterHotelViewModelBasedOnFilters(
      hotelViewModels,
      nameSearchParam || '',
      priceOptions,
      !!isTodayDealsSearchParam,
      selectedUsdPrices,
      selectedReviewScores,
      selectedAmenityIds,
      selectedBookingOptions,
      selectedBrandIds,
      selectedChainIds,
      selectedDistrictIds,
      selectedPropertyTypeIds,
      selectedRateAmenityIds,
      selectedReviewerGroups,
      selectedStarIds,
      selectedRateProviderCodes,
    );

    let filteredSponsoredAdHotelViewModels: Array<HotelViewModel> =
      filterHotelViewModelBasedOnFilters(
        hotelMetaSearch.sponsoredAdHotelViewModels,
        nameSearchParam || '',
        priceOptions,
        !!isTodayDealsSearchParam,
        selectedUsdPrices,
        selectedReviewScores,
        selectedAmenityIds,
        selectedBookingOptions,
        selectedBrandIds,
        selectedChainIds,
        selectedDistrictIds,
        selectedPropertyTypeIds,
        selectedRateAmenityIds,
        selectedReviewerGroups,
        selectedStarIds,
        selectedRateProviderCodes,
      );

    if (sortType !== HotelSortType.Recommended) {
      filteredHotelViewModels = [...filteredHotelViewModels, ...filteredSponsoredAdHotelViewModels];
    }

    const sortedHotelViewModels$ = sortHotelViewModels(
      filteredHotelViewModels,
      sortType,
      sortOrder,
      priceOptions,
    );

    if (sortType === HotelSortType.Recommended) {
      filteredSponsoredAdHotelViewModels.forEach((sponsoredAdHotelViewModel, index) => {
        if (index === 0) {
          sortedHotelViewModels$.splice(0, 0, sponsoredAdHotelViewModel);
        } else if (index === 1) {
          sortedHotelViewModels$.splice(6, 0, sponsoredAdHotelViewModel);
        }
      });
    }

    return sortedHotelViewModels$;
  }, [
    hotelMetaSearch.hotelViewModels,
    hotelMetaSearch.sponsoredAdHotelViewModels,
    priceOptions,
    nameSearchParam || '',
    sortType,
    sortOrder,
    isTodayDealsSearchParam,
    selectedUsdPrices,
    selectedReviewScores,
    selectedAmenityIds,
    selectedBookingOptions,
    selectedBrandIds,
    selectedChainIds,
    selectedDistrictIds,
    selectedHotelIds,
    selectedPropertyTypeIds,
    selectedRateAmenityIds,
    selectedReviewerGroups,
    selectedStarIds,
    selectedRateProviderCodes,
    wegoHotelIdSearchParam,
    providerSearchParam,
  ]);

  const rowCount = useMemo<number>(() => {
    if (sortedAndFilteredHotelViewModels.length > 8) {
      return sortedAndFilteredHotelViewModels.length + 3;
    } else if (sortedAndFilteredHotelViewModels.length > 5) {
      return sortedAndFilteredHotelViewModels.length + 2;
    } else if (sortedAndFilteredHotelViewModels.length > 2) {
      return sortedAndFilteredHotelViewModels.length + 1;
    } else {
      return sortedAndFilteredHotelViewModels.length;
    }
  }, [sortedAndFilteredHotelViewModels]);
  //#endregion

  const topAdPosition = newHotelInTownAdHeight ? 3 : 4;
  const travelerTypePosition = 7;
  const middleAdPosition = 8;
  const bottomAdPosition = 12;

  const topAdHeight = newHotelInTownAdHeight || nativeTopAdHeight || topLeaderboardAdsHeight;
  const middleAdHeight = nativeMiddleAdHeight ? nativeMiddleAdHeight : middleLeaderboardAdsHeight;
  const bottomAdHeight = nativeBottomAdHeight ? nativeBottomAdHeight : bottomLeaderboardAdsHeight;

  const adsTypePosition: { [position: number]: 'banner' | 'native' } = useMemo(() => {
    let position = {};
    let belowMiddleAdPosition = middleAdPosition;
    let belowBottomAdPosition = bottomAdPosition;

    if (topAdHeight && sortedAndFilteredHotelViewModels.length >= 3) {
      position = {
        ...position,
        [topAdPosition]: nativeTopAdHeight || newHotelInTownAdHeight ? 'native' : 'banner',
      };
      belowMiddleAdPosition--;
      belowBottomAdPosition--;
    }
    if (isShowTravelerBox && isOpenTravelerTypeBox) {
      belowMiddleAdPosition--;
      belowBottomAdPosition--;
    }
    if (
      middleAdHeight &&
      sortedAndFilteredHotelViewModels.length > 6 &&
      !sortedAndFilteredHotelViewModels[6].sponsoredAd
    ) {
      belowBottomAdPosition--;
      position = {
        ...position,
        [belowMiddleAdPosition]: nativeMiddleAdHeight ? 'native' : 'banner',
      };
    }
    if (bottomAdHeight && sortedAndFilteredHotelViewModels.length > 8) {
      if (!middleAdHeight || sortedAndFilteredHotelViewModels[6].sponsoredAd) {
        belowBottomAdPosition--;
      }
      position = {
        ...position,
        [belowBottomAdPosition]: nativeBottomAdHeight ? 'native' : 'banner',
      };
    }
    return position;
  }, [
    topAdHeight,
    middleAdHeight,
    bottomAdHeight,
    nativeTopAdHeight,
    nativeMiddleAdHeight,
    nativeBottomAdHeight,
    sortedAndFilteredHotelViewModels,
    isShowTravelerBox,
    isOpenTravelerTypeBox,
  ]);

  const topAdTop = useMemo<number>(() => {
    let heightOfGoogleHotelViewModels = 0;

    if (googleHotelViewModels.length > 0) {
      /**
       * 292 the height of Google hotel
       * 32 the spacing between property and more properties label
       * 48 the label's height plus margin bottom
       */
      heightOfGoogleHotelViewModels = 292 + 32 + 48;
    }

    let heightOfSelectedHotelViewModels = 0;

    if (selectedHotelViewModels.length > 0) {
      // 63 is the height of Selected property label and vertical line that separate it from the result minus 8 (because last selected hotel card does not have bottom gap)
      // 32 is the height of similar properties label (22) and margin bottom (10). Only display if there are more than 1 selected hotel.
      heightOfSelectedHotelViewModels = selectedHotelViewModels.length * 248 + 63 + 32;
    }

    let heightOfEgpytBankNotice = 0;

    if (hasEgyptCentralBankNotice) {
      // 64 height of div egpytCentralBankNotice
      // 12 margin bottom of egpytCentralBankNotice
      heightOfEgpytBankNotice = 64 + 12;
    }

    let sumOfHotelCardHeightAboveAds = 248 * (topAdPosition - 1);

    const firstHotelViewModel = sortedAndFilteredHotelViewModels.at(0);

    if (!!firstHotelViewModel && !!firstHotelViewModel.sponsoredAd) {
      // 306 is the height of sponsored ads hotel card
      sumOfHotelCardHeightAboveAds = 306 + 248 * (topAdPosition - 2);
    }

    // 48 + 12 is the height of sort bar plus margin bottom
    // 90 is the height of the login banner
    return (
      60 +
      (!user ? 90 : 0) +
      heightOfGoogleHotelViewModels +
      heightOfSelectedHotelViewModels +
      heightOfEgpytBankNotice +
      sumOfHotelCardHeightAboveAds
    );
  }, [
    topAdPosition,
    windowInnerWidth,
    user,
    sortedAndFilteredHotelViewModels,
    googleHotelViewModels,
    selectedHotelViewModels,
    hasEgyptCentralBankNotice,
    isMapClosed,
  ]);

  const travelerTypeTop = useMemo<number>(() => {
    if (isShowTravelerBox && isOpenTravelerTypeBox) {
      let sumOfHotelCardHeightInBetween = 248 * (travelerTypePosition - topAdPosition - 1);

      // 8 is the space between each card
      return topAdTop + (!!topAdHeight ? topAdHeight + 12 : 4) + sumOfHotelCardHeightInBetween;
    }
    return 0;
  }, [topAdTop, topAdHeight, isShowTravelerBox, isOpenTravelerTypeBox]);

  const middleAdTop = useMemo<number>(() => {
    let sumOfHotelCardHeightInBetween = 248 * (middleAdPosition - topAdPosition - 1);

    // 8 is the space between each card
    return travelerTypeTop > 0
      ? travelerTypeTop + 440 + 12
      : topAdTop + (!!topAdHeight ? topAdHeight + 8 : 0) + sumOfHotelCardHeightInBetween;
  }, [topAdTop, topAdHeight, travelerTypeTop]);

  const bottomAdTop = useMemo<number>(() => {
    let sumOfHotelCardHeightInBetween = 248 * (bottomAdPosition - middleAdPosition - 1);

    // 8 is the space between each card
    const hotelViewModel = sortedAndFilteredHotelViewModels[6];

    return (
      middleAdTop +
      ((!!hotelViewModel && hotelViewModel.sponsoredAd) || !middleAdHeight
        ? 0
        : middleAdHeight + 8) +
      sumOfHotelCardHeightInBetween
    );
  }, [middleAdTop, sortedAndFilteredHotelViewModels, middleAdHeight]);

  const errorAlertContents = useMemo(() => {
    switch (hotelMetaSearchError.status) {
      case '500':
        return {
          title: translations.server_error_header,
          message: translations.error_server_message,
          buttonLabel: translations.click_to_retry,
        };
      case '503':
        return {
          title: translations.server_error_header_temporarily,
          message: translations.server_error_message_temporarily,
          buttonLabel: '',
        };
      default:
        return {
          title: translations.server_error_header,
          message: translations.error_server_message,
          buttonLabel: translations.click_to_retry,
        };
    }
  }, [hotelMetaSearchError]);

  const handleOnSortTypeChanged = useCallback<
    (hotelSortType: HotelSortType, sortOrder: SortOrder) => void
  >(
    (hotelSortType: HotelSortType, sortOrder: SortOrder) => {
      searchParams.delete('sort');
      searchParams.set('sort', getHotelSortTypeSearchParamValue(hotelSortType));

      searchParams.delete('order');
      searchParams.set('order', sortOrder);

      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...(locationState.eventTrackingObj && locationState.eventTrackingObj),
              current_sort: getHotelSortTypeEventValue(hotelSortType, sortOrder),
            },
            impressionType: 'sort',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleOnSortDropdownOpen = () => {
    const eventData = {
      id: pageViewId,
      category: 'search_results_options',
      object: 'sort',
      action: 'open',
      value: '',
    };
    genzoTrackActionEvent(
      apiBaseUrl,
      wegoAnalyticsClientId!,
      wegoAnalyticsClientSessionId!,
      user?.userHash,
      eventData,
    );
  };

  const handleRefreshSearch = useCallback<(triggerSearchCounter: number) => void>(
    (triggerSearchCounter: number) => {
      dispatch(updateTriggerSearchCounter(triggerSearchCounter + 1));
    },
    [],
  );

  const handleMapViewToggle = useCallback<(isMapView: boolean) => void>(
    (isMapView: boolean) => {
      searchParams.delete('view');

      if (isMapView) {
        setIsMapClosed(false);
        searchParams.set('view', 'map');

        const eventData = {
          id: pageViewId,
          category: 'map',
          object: 'hotel_results',
          action: 'click',
          value: 'open',
        };

        genzoTrackActionEvent(
          apiBaseUrl,
          wegoAnalyticsClientId!,
          wegoAnalyticsClientSessionId!,
          user?.userHash,
          eventData,
        );
      }

      setIsFiltersDrawerOpen(false);

      navigate(
        { pathname, search: searchParams.toString() },
        { replace: true, state: locationState },
      );
    },
    [
      pageViewId,
      pathname,
      locationState,
      user,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const handleOnHotelCardMapClick = useCallback(
    (hotelId?: number) => {
      const eventData = {
        id: pageViewId,
        category: 'map',
        object: hotelId ? 'hotel_info_window' : 'hotel_card_pop_up',
        action: 'click',
        value: hotelId?.toString() ?? 'hotel detail',
      };

      genzoTrackActionEvent(
        apiBaseUrl,
        wegoAnalyticsClientId!,
        wegoAnalyticsClientSessionId!,
        user?.userHash,
        eventData,
      );
    },
    [pageViewId, wegoAnalyticsClientId, wegoAnalyticsClientSessionId, user],
  );

  const handleOnHotelCardClick = useCallback<
    (
      selectedRate: RateViewModel,
      hotelViewModel: HotelViewModel,
      index: number,
      typeOfClick: 'sponsoredAd' | 'rate' | 'detail',
    ) => void
  >(
    (selectedRate, hotelViewModel, index, typeOfClick) => {
      if ((typeOfClick === 'sponsoredAd' || typeOfClick === 'rate') && !!nearestCityByIP) {
        webEngageTrackRateSearchResult(
          currentSite.countryCode,
          hotelViewModel.hotel,
          hotelViewModel.amenities,
          hotelMetaSearch.search!,
          locale,
          selectedRate,
          window.location.href,
          nearestCityByIP,
        );

        if (!selectedRate.provider.directBooking) {
          webEngageTrackPurchase();
        }
      }

      impressionLogHotelCardClick(
        typeOfClick,
        apiBaseUrl,
        wegoAnalyticsClientId!,
        wegoAnalyticsClientSessionId!,
        eventIdRef.current,
        hotelViewModel,
        priceOptions,
        pageViewId,
        hotelMetaSearch.search!.id,
        index + 1,
        adsTypePosition,
        selectedRate,
        user?.userHash,
      );
    },
    [
      adsTypePosition,
      currentSite,
      hotelMetaSearch.search,
      locale,
      nearestCityByIP,
      priceOptions,
      user,
    ],
  );

  //#region Filter toggle handlers
  const handleOnClearFilter = useCallback<() => void>(() => {
    searchParams.delete('name');
    searchParams.delete('deals');
    searchParams.delete('amenities');
    searchParams.delete('providers');
    searchParams.delete('brands');
    searchParams.delete('chains');
    searchParams.delete('districts');
    searchParams.delete('price');
    searchParams.delete('accommodation_types');
    searchParams.delete('free_amenities');
    searchParams.delete('guest_rating');
    searchParams.delete('guest_score');
    searchParams.delete('stars');
    searchParams.delete('provider_codes');

    const eventData = {
      id: pageViewId,
      category: 'search_results_options',
      object: 'filter',
      action: 'reset',
      value: JSON.stringify({
        ...locationState?.eventTrackingObj,
        popular_filters: !!popularFiltersRef.current ? popularFiltersRef.current : undefined,
      }),
    };
    const eventId = generateCUID();
    eventIdRef.current = eventId;
    popularFiltersRef.current = null;

    genzoTrackActionEvent(
      apiBaseUrl,
      wegoAnalyticsClientId!,
      wegoAnalyticsClientSessionId!,
      user?.userHash,
      eventData,
      eventId,
    );
    navigate(
      { pathname, search: searchParams.toString() },
      {
        replace: true,
        state: {
          ...locationState,
          impressionType: 'filter',
          eventTrackingObj: undefined,
        },
      },
    );
  }, [pathname, locationState]);

  const handleOnPopularFilterClick = (selectedFilter: string) => {
    const currentFilter = popularFiltersRef?.current || [];

    if (currentFilter.includes(selectedFilter)) {
      popularFiltersRef.current = currentFilter.filter((filter) => filter !== selectedFilter);
    } else {
      popularFiltersRef.current = [...currentFilter, selectedFilter];
    }
  };

  const handleHotelNameSearchQueryChanged = useCallback<
    (event: React.ChangeEvent<HTMLInputElement>) => void
  >(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const hotelNameSearchQuery = event.target.value;

      searchParams.delete('name');

      if (hotelNameSearchQuery.length > 0) {
        searchParams.set('name', hotelNameSearchQuery);
      }

      navigate(
        { pathname, search: searchParams.toString() },
        { replace: true, state: { ...locationState, impressionType: 'filter' } },
      );
    },
    [pathname, locationState],
  );

  const handleIsTodayDealsToggle = useCallback<(isSelected: boolean) => void>(
    (isSelected: boolean) => {
      searchParams.delete('deals');

      if (isSelected) {
        searchParams.set('deals', 'today');
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              todays_deals: isSelected ? 1 : 0,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, locationState],
  );

  const handleSelectedUsdPricesChanged = useCallback<(usdPrices: Array<number>) => void>(
    (usdPrices: Array<number>) => {
      let eventTrackingObj: {
        price_min_max_usd: number[];
        price_selected_usd: number[];
      } = {
        price_min_max_usd: [],
        price_selected_usd: [],
      };
      searchParams.delete('price');

      if (
        minUsdPrice &&
        maxUsdPrice &&
        !(usdPrices[0] === minUsdPrice && usdPrices[1] === maxUsdPrice)
      ) {
        searchParams.set('price', usdPrices.join(','));
        eventTrackingObj = {
          price_min_max_usd: [minUsdPrice, maxUsdPrice],
          price_selected_usd: usdPrices,
        };
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            impressionType: 'filter',
            ...(!!minUsdPrice &&
              !!maxUsdPrice && {
                eventTrackingObj: {
                  ...locationState.eventTrackingObj,
                  ...eventTrackingObj,
                },
              }),
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleIsIncludingTaxChanged = useCallback<(isIncludingTax: boolean) => void>(
    (isIncludingTax: boolean) => {
      dispatch(changeIsIncludingTaxOrFee(isIncludingTax));
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              tax_included: isIncludingTax ? 1 : 0,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [dispatch, pathname, search, locationState],
  );

  const handleShowTotalPriceChanged = (showTotalPrice: boolean) => {
    dispatch(changeShowTotalPrice(showTotalPrice));
    navigate(
      { pathname, search: searchParams.toString() },
      {
        replace: true,
        state: {
          ...locationState,
          eventTrackingObj: {
            ...(locationState?.eventTrackingObj && locationState.eventTrackingObj),
            price_options: showTotalPrice ? 'total_price' : 'per_night',
          },
          impressionType: 'filter',
        },
      },
    );
  };

  const handleSelectedReviewScoresChanged = useCallback<(reviewScores: Array<number>) => void>(
    (reviewScores: Array<number>) => {
      searchParams.delete('guest_score');

      if (
        !(
          reviewScores[0] === reviewScoresRange[0] &&
          reviewScores[1] === reviewScoresRange[reviewScoresRange.length - 1]
        )
      ) {
        searchParams.set(
          'guest_score',
          reviewScores
            .map(
              (reviewScore) => reviewScoresRange[findClosest(reviewScoresRange, reviewScore) ?? 0],
            )
            .join(','),
        );
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              review_scores: reviewScores,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleAmenityFilterToggle = useCallback<(amenityId: number, isSelected: boolean) => void>(
    (amenityId: number, isSelected: boolean) => {
      const cloneSelectedAmenityIds = [...selectedAmenityIds];

      if (isSelected) {
        cloneSelectedAmenityIds.push(amenityId);
      } else {
        cloneSelectedAmenityIds.splice(cloneSelectedAmenityIds.indexOf(amenityId), 1);
      }
      const selectedAmenitiesName = cloneSelectedAmenityIds.map(
        (selectedAmenity) =>
          hotelMetaSearch.amenitiesFilter.find((amenity) => amenity.id === selectedAmenity)?.name,
      );
      searchParams.delete('amenities');

      if (cloneSelectedAmenityIds.length > 0) {
        searchParams.set('amenities', cloneSelectedAmenityIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              amenities: selectedAmenitiesName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState, hotelMetaSearch.amenitiesFilter],
  );

  const handleBookingOptionFilterToggle = useCallback<
    (bookingOption: string, isSelected: boolean) => void
  >(
    (bookingOption: string, isSelected: boolean) => {
      const cloneSelectedBookingOptions = [...selectedBookingOptions];

      if (isSelected) {
        cloneSelectedBookingOptions.push(bookingOption);
      } else {
        cloneSelectedBookingOptions.splice(cloneSelectedBookingOptions.indexOf(bookingOption), 1);
      }
      const bookingOptionsName = cloneSelectedBookingOptions.map((option) => {
        switch (option) {
          case 'hotels':
            return 'hotel_website';
          case 'ota':
            return 'travel_agency';
          case 'wego':
            return 'bow';
          default:
            return;
        }
      });
      searchParams.delete('providers');

      if (cloneSelectedBookingOptions.length > 0) {
        searchParams.set('providers', cloneSelectedBookingOptions.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              booking_options: bookingOptionsName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleBrandFilterToggle = useCallback<(brandId: number, isSelected: boolean) => void>(
    (brandId: number, isSelected: boolean) => {
      const cloneSelectedBrandIds = [...selectedBrandIds];

      if (isSelected) {
        cloneSelectedBrandIds.push(brandId);
      } else {
        cloneSelectedBrandIds.splice(cloneSelectedBrandIds.indexOf(brandId), 1);
      }

      searchParams.delete('brands');

      if (cloneSelectedBrandIds.length > 0) {
        searchParams.set('brands', cloneSelectedBrandIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              brands: cloneSelectedBrandIds,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleChainFilterToggle = useCallback<(chainId: number, isSelected: boolean) => void>(
    (chainId: number, isSelected: boolean) => {
      const cloneSelectedChainIds = [...selectedChainIds];

      if (isSelected) {
        cloneSelectedChainIds.push(chainId);
      } else {
        cloneSelectedChainIds.splice(cloneSelectedChainIds.indexOf(chainId), 1);
      }

      searchParams.delete('chains');

      if (cloneSelectedChainIds.length > 0) {
        searchParams.set('chains', cloneSelectedChainIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              chains: cloneSelectedChainIds,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleDistrictFilterToggle = useCallback<(districtId: number, isSelected: boolean) => void>(
    (districtId: number, isSelected: boolean) => {
      const cloneSelectedDistrictIds = [...selectedDistrictIds];

      if (isSelected) {
        cloneSelectedDistrictIds.push(districtId);
      } else {
        cloneSelectedDistrictIds.splice(cloneSelectedDistrictIds.indexOf(districtId), 1);
      }

      searchParams.delete('districts');

      if (cloneSelectedDistrictIds.length > 0) {
        searchParams.set('districts', cloneSelectedDistrictIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              districts: cloneSelectedDistrictIds,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handlePropertyTypeFilterToggle = useCallback<
    (propertyTypeId: number, isSelected: boolean) => void
  >(
    (propertyTypeId: number, isSelected: boolean) => {
      const cloneSelectedPropertyTypeIds = [...selectedPropertyTypeIds];

      if (isSelected) {
        cloneSelectedPropertyTypeIds.push(propertyTypeId);
      } else {
        cloneSelectedPropertyTypeIds.splice(
          cloneSelectedPropertyTypeIds.indexOf(propertyTypeId),
          1,
        );
      }
      const selectedPropertiesTypeName = cloneSelectedPropertyTypeIds.map(
        (typeId) =>
          hotelMetaSearch.propertyTypesFilter.find((property) => property.id === typeId)?.name,
      );
      searchParams.delete('accommodation_types');

      if (cloneSelectedPropertyTypeIds.length > 0) {
        searchParams.set('accommodation_types', cloneSelectedPropertyTypeIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              property_types: selectedPropertiesTypeName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleRateAmenityFilterToggle = useCallback<
    (rateAmenityId: number, isSelected: boolean) => void
  >(
    (rateAmenityId: number, isSelected: boolean) => {
      const cloneSelectedRateAmenityIds = [...selectedRateAmenityIds];

      if (isSelected) {
        cloneSelectedRateAmenityIds.push(rateAmenityId);
      } else {
        cloneSelectedRateAmenityIds.splice(cloneSelectedRateAmenityIds.indexOf(rateAmenityId), 1);
      }
      const selectedRateAmenityName = cloneSelectedRateAmenityIds.map(
        (selectedRateAmenityId) =>
          hotelMetaSearch.rateAmenitiesFilter.find(
            (rateAmenity) => rateAmenity.id === selectedRateAmenityId,
          )?.name,
      );
      searchParams.delete('free_amenities');

      if (cloneSelectedRateAmenityIds.length > 0) {
        searchParams.set('free_amenities', cloneSelectedRateAmenityIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              rate_types: selectedRateAmenityName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState, hotelMetaSearch.rateAmenitiesFilter],
  );

  const handleReviewerGroupFilterToggle = useCallback<
    (reviewerGroup: string, isSelected: boolean) => void
  >(
    (reviewerGroup: string, isSelected: boolean) => {
      const cloneSelectedReviewerGroups = [...selectedReviewerGroups];

      if (isSelected) {
        cloneSelectedReviewerGroups.push(reviewerGroup);
      } else {
        cloneSelectedReviewerGroups.splice(cloneSelectedReviewerGroups.indexOf(reviewerGroup), 1);
      }

      searchParams.delete('guest_rating');

      if (cloneSelectedReviewerGroups.length > 0) {
        searchParams.set('guest_rating', cloneSelectedReviewerGroups.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              review_groups: cloneSelectedReviewerGroups,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleStarFilterToggle = useCallback<(starId: number, isSelected: boolean) => void>(
    (starId: number, isSelected: boolean) => {
      const cloneSelectedStarIds = [...selectedStarIds];

      if (isSelected) {
        cloneSelectedStarIds.push(starId);
      } else {
        cloneSelectedStarIds.splice(cloneSelectedStarIds.indexOf(starId), 1);
      }

      searchParams.delete('stars');

      if (cloneSelectedStarIds.length > 0) {
        searchParams.set('stars', cloneSelectedStarIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              stars: cloneSelectedStarIds.sort(),
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const handleRateProviderToggle = useCallback<
    (rateProviderCode: string, isSelected: boolean) => void
  >(
    (rateProviderCode: string, isSelected: boolean) => {
      const cloneSelectedRateProviderCodes = [...selectedRateProviderCodes];

      if (isSelected) {
        cloneSelectedRateProviderCodes.push(rateProviderCode);
      } else {
        cloneSelectedRateProviderCodes.splice(
          cloneSelectedRateProviderCodes.indexOf(rateProviderCode),
          1,
        );
      }
      const selectedProviderName = cloneSelectedRateProviderCodes.map(
        (selectedProviderCode) =>
          hotelMetaSearch.rateProviders.find((provider) => provider.code === selectedProviderCode)
            ?.name,
      );
      searchParams.delete('provider_codes');

      if (cloneSelectedRateProviderCodes.length > 0) {
        searchParams.set('provider_codes', cloneSelectedRateProviderCodes.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              booking_sites: selectedProviderName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState, hotelMetaSearch.rateProviders],
  );
  //#endregion

  const getRowHeight = useCallback<({ index }: Index) => number>(
    ({ index }: Index) => {
      if (
        index === rowCount - 1 &&
        !!sortedAndFilteredHotelViewModels[index] &&
        !sortedAndFilteredHotelViewModels[index].sponsoredAd
      ) {
        return 242;
      }

      if (index === 0 && !!sortedAndFilteredHotelViewModels[index].sponsoredAd) {
        return 306;
      }

      if (index === topAdPosition - 1) {
        // 8 is the space between each card
        return topAdHeight ? topAdHeight + 8 : 0;
      }

      if (index === travelerTypePosition - 1 && travelerTypeTop > 0) {
        // 16 is the space between each card
        return 440 + 16;
      }

      if (index === middleAdPosition - 1) {
        const hotelViewModel = sortedAndFilteredHotelViewModels[6];

        if (!!hotelViewModel && hotelViewModel.sponsoredAd) {
          return 0;
        }
        // 8 is the space between each card
        return middleAdHeight ? middleAdHeight + 8 : 0;
      }

      if (index === bottomAdPosition - 1) {
        // 8 is the space between each card
        return bottomAdHeight ? bottomAdHeight + 8 : 0;
      }

      return 248;
    },
    [
      rowCount,
      sortedAndFilteredHotelViewModels,
      topAdHeight,
      middleAdHeight,
      bottomAdHeight,
      travelerTypeTop,
    ],
  );

  const handleMouseEnterOnCard = (hotelHovered: HotelViewModel) => {
    hoverTimer = setTimeout(() => {
      setHotelHovered(hotelHovered);
    }, 200);
  };

  const handleMouseLeaveOnCard = () => {
    clearTimeout(hoverTimer);
    setHotelHovered(undefined);
  };

  const rowRenderer = useCallback<({ key, index, style }: ListRowProps) => JSX.Element | null>(
    ({ key, index, style }: ListRowProps) => {
      if (
        index === topAdPosition - 1 ||
        index === middleAdPosition - 1 ||
        index === bottomAdPosition - 1 ||
        (index === travelerTypePosition - 1 && travelerTypeTop > 0)
      ) {
        return null;
      }
      let hotelViewModelIndex = index;

      if (index > 10) {
        if (travelerTypeTop > 0) {
          hotelViewModelIndex = index - 4;
        } else {
          hotelViewModelIndex = index - 3;
        }
      } else if (index > 6) {
        if (travelerTypeTop > 0) {
          hotelViewModelIndex = index - 3;
        } else {
          hotelViewModelIndex = index - 2;
        }
      } else if ((!newHotelInTownAdHeight && index > 3) || (newHotelInTownAdHeight && index > 2)) {
        hotelViewModelIndex = index - 1;
      }

      const hotelViewModel: HotelViewModel = sortedAndFilteredHotelViewModels[hotelViewModelIndex];

      const hotelLatestReviews: Array<HotelLatestReviews> | undefined =
        hotelsLatestReviews?.[hotelViewModel.hotelId];

      return (
        <div
          key={key}
          style={style}
          onMouseEnter={() => handleMouseEnterOnCard(hotelViewModel)}
          onMouseLeave={handleMouseLeaveOnCard}
        >
          <HotelCard
            hasTabbyGateway={hasTabbyGateway}
            hotelViewModel={hotelViewModel}
            hotelLatestReviews={hotelLatestReviews}
            localeParam={localeParam}
            onHotelCardClick={(selectedRate, hotelViewModel, typeOfClick) => {
              handleOnHotelCardClick(
                selectedRate,
                hotelViewModel,
                hotelViewModelIndex,
                typeOfClick,
              );
            }}
            searchId={hotelMetaSearch.search!.id}
            showSponsoredAd={index === 0 && !!hotelViewModel.sponsoredAd}
            wegoAnalyticsClientId={wegoAnalyticsClientId}
            wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            isShowReview={listRenderId[hotelViewModel.hotelId]}
            setListRenderId={setListRenderId}
          />
        </div>
      );
    },
    [
      sortedAndFilteredHotelViewModels,
      currency,
      exchangeRate,
      hasTabbyGateway,
      hotelMetaSearch,
      isRtl,
      locale,
      localeParam,
      newHotelInTownAdHeight,
      priceOptions,
      sortedAndFilteredHotelViewModels,
      translations,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
      newHotelInTownAdHeight,
      listRenderId,
      travelerTypeTop,
    ],
  );

  const handleHotelSearchResultOnBack = useCallback((): void => {
    dispatch(resetHotelMetaSearch());

    searchParams.delete('payment_methods');
    searchParams.delete('sort');
    searchParams.delete('order');
    searchParams.delete('flexibilities');
    searchParams.delete('full_card');
    searchParams.delete('name');
    searchParams.delete('deals');
    searchParams.delete('amenities');
    searchParams.delete('providers');
    searchParams.delete('brands');
    searchParams.delete('chains');
    searchParams.delete('districts');
    searchParams.delete('price');
    searchParams.delete('accommodation_types');
    searchParams.delete('free_amenities');
    searchParams.delete('guest_rating');
    searchParams.delete('guest_score');
    searchParams.delete('stars');
    searchParams.delete('provider_codes');
    searchParams.delete('wego_growth');

    navigate(
      {
        pathname: hotelsRoute(currentSite, locale),
        search: searchParams.toString(),
      },
      { replace: true, state },
    );
  }, [pathname, search, locale, state]);

  const handleClearHotelSearchName = () => {
    searchParams.delete('name');
    searchboxRef.current?.focus();
    navigate({ pathname, search: searchParams.toString() }, { replace: true, state });
  };

  const targetParams: { [key: string]: string } | undefined = useMemo(() => {
    if (!hotelMetaSearch.search) return;
    const hostname = window.location.hostname;
    const hotelSearchResultSearch = hotelMetaSearch.search;

    const { adultCount, childCount } = hotelSearchResultSearch.rooms.reduce<{
      adultCount: number;
      childCount: number;
    }>(
      (accumulator, currentValue) => {
        accumulator.adultCount += currentValue.adultsCount;
        accumulator.childCount += currentValue.childrenCount;

        return accumulator;
      },
      { adultCount: 0, childCount: 0 },
    );

    const checkInDateMilliseconds = getLocalDate(hotelSearchResultSearch.checkIn).getTime();
    const checkOutDateMilliseconds = getLocalDate(hotelSearchResultSearch.checkOut).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const leadtimeDays = numberOfNights(today.getTime(), checkInDateMilliseconds);

    return {
      _adults: adultCount.toString(),
      _cctld: currentSite.countryCode,
      _check_in: hotelSearchResultSearch.checkIn,
      _check_out: hotelSearchResultSearch.checkOut,
      ...(!!starIdsSearchParam && { _stars: starIdsSearchParam }),
      _children: childCount.toString(),
      _currency_conversion: (1 / exchangeRate.rate).toString(),
      _currency_symbol: currency.symbol,
      _currency: currency.code,
      _duration: numberOfNights(checkInDateMilliseconds, checkOutDateMilliseconds).toString(),
      _env:
        isDevelopmentEnvironment(hostname) || isStagingEnvironment(hostname)
          ? 'staging'
          : 'production',
      _guests: hotelSearchResultSearch.guestsCount.toString(),
      _is_new: `${!Boolean(getCookie('wego_visits_cnt'))}`,
      _lead_time: leadtimeDays.toString(),
      _locale: locale,
      _logged_in: !!user ? 'true' : 'false',
      _page_type: 'search results',
      _product: 'hotels',
      _rooms: hotelSearchResultSearch.roomsCount.toString(),
      _to_acode: hotelSearchResultSearch.cityCode!,
      _to_city: hotelSearchResultSearch.cityName!,
      _to_ctry_code: hotelSearchResultSearch.country?.code,
      _to_ctry: hotelSearchResultSearch.country?.name.toLowerCase(),
      _viewport_breakpoint: window.innerWidth.toString(),
      ...(!!hotelSearchResultSearch.city && {
        _to_city_locale: hotelSearchResultSearch.city.name.toLowerCase(),
      }),
      ...(!!hotelSearchResultSearch.region && {
        _to_rgn: hotelSearchResultSearch.region.name.toLowerCase(),
      }),
    };
  }, [currency, exchangeRate, hotelMetaSearch.search?.id, locale, starIdsSearchParam, user]);

  const handleSlotRenderEnded = (event: googletag.events.SlotRenderEndedEvent): void => {
    const slotElementId = event.slot.getSlotElementId();
    const size = event.size;

    if (size !== null) {
      switch (slotElementId) {
        case 'top_leaderboard':
          setTopLeaderboardAdsHeight((size as Array<number>)[1]);
          break;
        case 'middle_leaderboard':
          setMiddleLeaderboardAdsHeight((size as Array<number>)[1]);
          break;
        case 'bottom_leaderboard':
          setBottomLeaderboardAdsHeight((size as Array<number>)[1]);
          break;
        case 'native_top_ad':
          setIsNativeTopAdRendered(true);
          break;
        case 'native_middle_ad':
          setIsNativeMiddleAdRendered(true);
          break;
        case 'native_bottom_ad':
          setIsNativeBottomAdRendered(true);
          break;
        case 'new_hotel_in_town':
          setIsNewHotelInTownAdRendered(true);
          break;
        default:
          if (/^(?:mrec_1|mrec_2|sticky_top|sticky_bottom)$/.test(slotElementId)) {
            if (slotElementId === 'sticky_top' && size[1] === 250) {
              setShowStickyBottom(true);
            }
            setHasMrecOrStickyAds(true);
          }
          return;
      }
    }
  };

  useGPTAdsInitialization(
    hotelMetaSearch,
    isMapVisible,
    targetParams,
    isScreenBetween1200And1340,
    isScreenAbove1340,
    setGoogleSlots,
    setHasMrecOrStickyAds,
    setTopLeaderboardAdsHeight,
    setMiddleLeaderboardAdsHeight,
    setBottomLeaderboardAdsHeight,
    handleSlotRenderEnded,
  );

  useGPTAdsRefresh(googleSlots);

  useEffect(() => {
    // Scroll to top when page load
    window.scrollTo(0, 0);

    const initializeWindowInnerWidth = (): void => {
      setWindowInnerWidth(window.innerWidth);
    };

    window.addEventListener('resize', initializeWindowInnerWidth);

    initializeWindowInnerWidth();

    return () => {
      window.removeEventListener('resize', initializeWindowInnerWidth);
    };
  }, []);

  useEffect(() => {
    if (!pageViewId) return;

    dispatch(changeHomePageViewId(pageViewId));
  }, [pageViewId]);

  // Redirect with search param if any one of them is missing.
  useEffect(() => {
    if (!guestsSearchParam || !sortTypeSearchParam || !sortOrderSearchParam || !isMapClosed) {
      const defaultSortType =
        !radiusLatParam && !radiusLongParam
          ? HotelSortType.Recommended
          : HotelSortType.NearestToTargetLocation;

      if (!guestsSearchParam) {
        searchParams.set(
          'guests',
          convertGuestRoomsToSearchParam([{ adultsCount: 2, childrenCount: 0, childrenAges: [] }]),
        );
      }

      if (!sortTypeSearchParam) {
        searchParams.set('sort', getHotelSortTypeSearchParamValue(defaultSortType));
      }

      if (!sortOrderSearchParam) {
        searchParams.set('order', 'desc');
      }

      if (!viewSearchParam && isHalfScreenMap && !isMapClosed) {
        searchParams.set('view', 'map');
      }

      if ((!radiusLatParam || !radiusLongParam) && sortTypeSearchParam === 'distance_to_location') {
        searchParams.delete('sort');
      }

      navigate(
        { pathname, search: searchParams.toString() },
        { replace: true, state: locationState },
      );
    }
  }, [
    pathname,
    JSON.stringify(locationState),
    guestsSearchParam,
    sortTypeSearchParam,
    sortOrderSearchParam,
    viewSearchParam,
    isHalfScreenMap,
    isMapClosed,
    radiusLatParam,
    radiusLongParam,
  ]);

  // Remove selected_hotel_ids and redirect if wego_hotel_id, provider, selected_hotel_ids all existed
  useEffect(() => {
    if (!!wegoHotelIdSearchParam && !!providerSearchParam && selectedHotelIds.length > 0) {
      searchParams.delete('selected_hotel_ids');
      navigate(
        { pathname, search: searchParams.toString() },
        { replace: true, state: locationState },
      );
    }
  }, [wegoHotelIdSearchParam, providerSearchParam, selectedHotelIds]);

  // WebEngage Tracking
  useEffect(() => {
    if (!isWebEngageTracked && !!hotelMetaSearch.search?.id && !!nearestCityByIP) {
      webEngageTrackHotelSearch(
        currentSite.countryCode,
        locale,
        nearestCityByIP,
        hotelMetaSearch.search,
        window.location.href,
      );
      setIsWebEngageTracked(true);
    }
  }, [isWebEngageTracked, hotelMetaSearch.search?.id, nearestCityByIP]);

  // Genzo Tracking
  useEffect(() => {
    if (
      !isGenzoTracked &&
      !!hotelMetaSearch.search?.id &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId
    ) {
      const { id, country, city, district, region, checkIn, checkOut, guestsCount, roomsCount } =
        hotelMetaSearch.search;
      const now = new Date();

      //#region Genzo v3
      const clientData: any = {
        id: wegoAnalyticsClientId,
        session_id: wegoAnalyticsClientSessionId,
        ...(!!user && {
          user_hash: user.userHash,
        }),
      };

      const pageData: any = {
        name: `Hotels Results|${country?.name}|${!!city ? city.name : ''}${
          !!region ? region.name : ''
        }`,
        product: 'hotels',
        base_type: 'hotels_search_results',
        sub_type: `hotels_search_results-new`,
        locale: locale,
        site_code: currentSite.countryCode,
        url: window.location.href,
        referrer_url: document.referrer,
      };

      const data: any = {
        client: clientData,
        created_at: toISOStringWithTimezone(now),
        event_id: id,
        id: pageViewId,
        page: pageData,
        external_services: {
          crm_id: !!user ? user.userHash : webEngageAnonymousId,
          ...(!!fbp && { fbp }),
          ...(!!fbc && { fbc }),
        },
      };

      genzoTrackPageView(apiBaseUrl, data);
      //#endregion

      //#region Genzo v2
      const clonePageData = { ...pageData };
      delete clonePageData.product;
      delete clonePageData.sub_type;

      const searchData: any = {
        id: id,
        client: {
          ...clientData,
          ...{
            app_type: 'WEB_APP',
            resolution: `${window.screen.availHeight}x${window.screen.availWidth}`,
          },
        },
        campaign: {
          sub_id: id,
          source: wegoSourceSearchParam,
          ts_code: getCookie('wego_ts_code') ?? null,
        },
        page: clonePageData,
        search: {
          ...(!!city && {
            city_code: city.code,
            city_name: city.name,
          }),
          ...(!!district && {
            district_name: district.name,
            district_id: district.id,
          }),
          ...(!!region && {
            region_id: region.id,
            region_name: region.name,
          }),
          check_in: checkIn,
          check_out: checkOut,
          country_code: country?.code,
          country_name: country?.name,
          guests_count: guestsCount,
          id: id,
          rooms_count: roomsCount,
        },
        created_at: now.toISOString(),
        search_mode: 'MANUAL',
      };

      if (!!user) {
        searchData.user = { email: user.email };
      }
      genzoTrack(`${apiBaseUrl}/genzo/v2/hotels/searches`, searchData);
      //#endregion

      setIsGenzoTracked(true);
    }
  }, [
    hotelMetaSearch.search?.id,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    pageViewId,
    webEngageAnonymousId,
  ]);

  // Impression Log: when page load, update history state
  useEffect(() => {
    navigate(
      { pathname, search: searchParams.toString() },
      {
        replace: true,
        state: {
          ...locationState,
          impressionType:
            hotelMetaSearch.search?.searchType === 'GEO_LOCATION' ? 'map_load' : 'page_load',
        },
      },
    );
  }, [hotelMetaSearch.search?.id]);

  // Impression Log: call endpoint impression log (filter/sort)
  useEffect(() => {
    const timerValues = {
      filter: 1000,
      map_load: 500,
      sort: 0,
      page_load: 0,
    };
    const timer = timerValues[locationState?.impressionType];

    const impressionLogTimer = setTimeout(() => {
      if (
        hotelMetaSearch.search?.id &&
        !!wegoAnalyticsClientId &&
        !!wegoAnalyticsClientSessionId &&
        !!locationState?.impressionType
      ) {
        impressionLogHotelSearchResult(
          locationState.impressionType,
          apiBaseUrl,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          eventIdRef.current,
          sortedAndFilteredHotelViewModels,
          priceOptions,
          pageViewId,
          hotelMetaSearch.search.id,
          adsTypePosition,
          user?.userHash,
        );
      }
    }, timer);

    return () => clearTimeout(impressionLogTimer);
  }, [sortedAndFilteredHotelViewModels]);

  // Todo: Remove old genzo visits
  useEffect(() => {
    if (
      !isGenzoVisitsTracked &&
      !!hotelMetaSearch.search?.id &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId
    ) {
      const { country, city, region } = hotelMetaSearch.search;

      const visitData: any = {
        id: generateCUID(),
        client: {
          app_type: 'WEB_APP',
          resolution: window.screen.availHeight + 'x' + window.screen.availWidth,
          id: wegoAnalyticsClientId,
          session_id: wegoAnalyticsClientSessionId,
          ...(!!user && { user_hash: user.userHash }),
        },
        campaign: { source: wegoSourceSearchParam, ts_code: getCookie('wego_ts_code') ?? null },
        page: {
          locale: locale,
          site_code: currentSite.countryCode,
          url: window.location.href,
          referrer_url: document.referrer,
          name: `Hotels Results|${country?.name}|${!!city ? city.name : ''}${
            !!region ? region.name : ''
          }`,
          base_type: 'hotels_search_results',
        },
        created_at: new Date().toISOString(),
        external_service: {
          crm_id: !!user ? user.userHash : webEngageAnonymousId,
          ...(!!fbp && { fbp }),
          ...(!!fbc && { fbc }),
        },
        ...(!!user && {
          user: {
            email: user.email,
          },
        }),
      };

      genzoTrack(`${apiBaseUrl}/genzo/v2/visits`, visitData);

      setIsGenzoVisitsTracked(true);
    }
  }, [
    hotelMetaSearch.search?.id,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    webEngageAnonymousId,
  ]);

  // Genzo event actions tracking for sort and filter
  useEffect(() => {
    if (
      !!locationState?.impressionType &&
      locationState.impressionType !== 'page_load' &&
      locationState.impressionType !== 'map_load' &&
      !!locationState.eventTrackingObj
    ) {
      const eventValue = {
        ...locationState?.eventTrackingObj,
        popular_filters: !!popularFiltersRef.current ? popularFiltersRef.current : undefined,
      };
      const eventId = generateCUID();
      eventIdRef.current = eventId;

      const eventData = {
        id: pageViewId,
        category: 'search_results_options',
        object: locationState?.impressionType,
        action: 'applied',
        value: JSON.stringify(eventValue),
      };
      genzoTrackActionEvent(
        apiBaseUrl,
        wegoAnalyticsClientId!,
        wegoAnalyticsClientSessionId!,
        user?.userHash,
        eventData,
        eventId,
      );
    }
  }, [locationState?.impressionType, JSON.stringify(locationState?.eventTrackingObj)]);

  // Gtm Tracking
  useEffect(() => {
    if (
      !isGtmTracked &&
      !!hotelMetaSearch.search?.id &&
      hotelMetaSearch.hotelViewModels.length > 0 &&
      hotelMetaSearch.cities.length > 0 &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId &&
      !!nearestCityByIP
    ) {
      const {
        id,
        country,
        city,
        checkIn,
        checkOut,
        guestsCount,
        roomsCount,
        district,
        region,
        searchType,
      } = hotelMetaSearch.search;

      const firstHotelViewModel = hotelMetaSearch.hotelViewModels[0];

      const pageData: HotelSearchResultGtmPageData = {
        check_in: checkIn,
        check_out: checkOut,
        children_count: convertGuestSearchParamToGuestRooms(guestsSearchParam!)
          .map((guestRoom) => guestRoom.childrenCount)
          .reduce((accumulator, currentValue) => accumulator + currentValue, 0),
        client_type: 'WEB_APP',
        country_code: country?.code,
        currency_code: currency.code,
        district_id: !!district ? district.id : '',
        domain: window.location.hostname,
        event: 'pageChanged',
        guest_count: guestsCount,
        hotel_id: firstHotelViewModel.hotelId,
        image_url_hotels: firstHotelViewModel.hotel.images[0] ?? '',
        locale: locale,
        page_name: `Hotels Results|${country?.name}|${!!city ? city.name : ''}${
          !!region ? region.name : ''
        }`,
        page_type: 'hotels_search_results',
        product: 'hotels',
        rates: firstHotelViewModel.rates[0] ? firstHotelViewModel.rates[0].price.amount : '',
        rooms_count: roomsCount,
        search_id: id,
        session_id: wegoAnalyticsClientSessionId,
        site_code: currentSite.countryCode,
        ts_code: getCookie('wego_ts_code') ?? null,
        url: window.location.href,
        user_country_code: nearestCityByIP.countryCode,
        user_id: getCookie('wego_user_id') ?? null,
        ...(!!user?.email && { e_id: sha256(user?.email).toString() }),
      };

      if (searchType === 'REGION') {
        const citiesHashMap = convertListToHashMap<{ code: string; name: string }>(
          hotelMetaSearch.cities,
          'code',
        );

        const locationIds: Array<string> = [];
        const locationNames: Array<string> = [];
        const locationCodes: Array<string> = [];

        region!.cityCodes.forEach((cityCode: string) => {
          const city = citiesHashMap[cityCode];

          if (!!city) {
            locationIds.push(city.code);
            locationNames.push(city.name);
            locationCodes.push(city.code);
          }
        });

        pageData.location_id = locationIds.join(':');
        pageData.location_name = locationNames.join(':');
        pageData.location_code = locationCodes.join(':');
      } else {
        pageData.location_id = city?.code;
        pageData.location_name = city?.name;
        pageData.location_code = city?.code;
      }

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, pageData);

      setIsGtmTracked(true);
    }
  }, [
    hotelMetaSearch.search?.id,
    hotelMetaSearch.hotelViewModels,
    hotelMetaSearch.cities,
    locale,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    nearestCityByIP,
    user,
  ]);

  // Gtm tracking google hotel for skycanner
  useEffect(() => {
    if (googleHotelViewModels.length > 0) {
      const googleHotelTopPrice = googleHotelViewModels[0].filteredRates[0].price;
      const googleHotelPrice = convertHotelRatePriceToPrice(googleHotelTopPrice, true, true);

      const gtmSkycanner: HotelGtmSkycannerTopDeal = {
        display_price: convertHotelRatePriceToPrice(
          googleHotelTopPrice,
          priceOptions.showTotalPrice,
          priceOptions.isIncludingTaxOrFee,
        ).amount,
        display_price_tax_inclusive: priceOptions.isIncludingTaxOrFee,
        display_price_type: priceOptions.showTotalPrice ? 'total_stay' : 'per_night',
        event: 'topDeal',
        fees: clamp(googleHotelTopPrice.totalLocalTaxAmount, 0, Infinity),
        taxes: googleHotelTopPrice.totalTaxAmount,
        total_price: googleHotelPrice.amount,
      };

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmSkycanner);
    }
  }, [googleHotelViewModels]);

  /**
   * As native ads size can be fluid, size returned from google will be [0,0] so need to get height after fluid ads become visible
   * More details https://developers.google.com/publisher-tag/guides/ad-sizes#fluid_ads
   */
  useEffect(() => {
    if (isNativeTopAdRendered) {
      const nativeTopAd = document.getElementById('native_top_ad');
      const nativeTopAdObserver = new ResizeObserver(() => {
        setNativeTopAdHeight(nativeTopAd?.clientHeight || 0);
      });
      nativeTopAdObserver.observe(nativeTopAd!);

      return () => nativeTopAdObserver.unobserve(nativeTopAd!);
    }
  }, [isNativeTopAdRendered]);

  useEffect(() => {
    if (isNativeMiddleAdRendered) {
      const nativeMiddleAd = document.getElementById('native_middle_ad');
      const nativeMiddleAdObserver = new ResizeObserver(() => {
        setNativeMiddleAdHeight(nativeMiddleAd?.clientHeight || 0);
      });
      nativeMiddleAdObserver.observe(nativeMiddleAd!);

      return () => nativeMiddleAdObserver.unobserve(nativeMiddleAd!);
    }
  }, [isNativeMiddleAdRendered]);

  useEffect(() => {
    if (isNativeBottomAdRendered) {
      const nativeBottomAd = document.getElementById('native_bottom_ad');
      const nativeBottomAdObserver = new ResizeObserver(() => {
        setNativeBottomAdHeight(nativeBottomAd?.clientHeight || 0);
      });
      nativeBottomAdObserver.observe(nativeBottomAd!);

      return () => nativeBottomAdObserver.unobserve(nativeBottomAd!);
    }
  }, [isNativeBottomAdRendered]);

  useEffect(() => {
    if (isNewHotelInTownAdRendered) {
      const hotelInTownAd = document.getElementById('new_hotel_in_town');
      const hotelInTownAdObserver = new ResizeObserver(() => {
        setNewHotelInTownAdHeight(hotelInTownAd?.clientHeight || 0);
      });
      hotelInTownAdObserver.observe(hotelInTownAd!);

      return () => hotelInTownAdObserver.unobserve(hotelInTownAd!);
    }
  }, [isNewHotelInTownAdRendered]);

  // Trigger hotel metasearch.
  useEffect(() => {
    const { checkInDate, checkOutDate, cityCode, districtId, regionId } = params;
    let isParamsValid = true;

    if (!/^(?:[0-9]{4}\-[0-9]{2}\-[0-9]{2})$/.test(checkInDate!)) {
      isParamsValid = false;
    }

    if (!/^(?:[0-9]{4}\-[0-9]{2}\-[0-9]{2})$/.test(checkOutDate!)) {
      isParamsValid = false;
    }

    if (!guestsSearchParam) {
      isParamsValid = false;
    }

    if (isParamsValid) {
      const searchType = !!regionId ? 'region' : !!districtId ? 'district' : 'city';

      const extraBodyParams: { [key: string]: string | boolean } = {
        ...(bowOnlySearchParam === 'true' && { bowOnly: true }),
      };

      const extraSearchBodyParams: { [key: string]: string | { [key: string]: string } | boolean } =
        {
          ...(!!wegoInternalCampaignSearchParam && isCrossSell && { crossSell: true }),
          ...(tsCodeSearchParam !== null && { tsCode: tsCodeSearchParam }),
          ...(!!wegoHotelIdSearchParam &&
            !!wegoPriceSearchParam && {
              encryptedDisplayedPriceByHotelId: { [wegoHotelIdSearchParam]: wegoPriceSearchParam },
            }),
          ...(wegoInternalCampaignSearchParam && {
            wgInternalCampaign: wegoInternalCampaignSearchParam,
          }),
          ...(wegoCampaignSearchParam && { wgCampaign: wegoCampaignSearchParam }),
          ...(wegoSourceSearchParam && { wgSource: wegoSourceSearchParam }),
          ...(wegoMediumSearchParam && { wgMedium: wegoMediumSearchParam }),
          ...(wegoGrowthSearchParam && { wgGrowth: 'dweb_cross_sell' }),
          ...(ghSearchParam && { ghParams: ghSearchParam }),
          ...(providerSearchParam && { ghProviderCode: providerSearchParam }),
        };

      const guestRooms = convertGuestSearchParamToGuestRooms(guestsSearchParam!);
      const requestParams = {
        url: `${apiBaseUrl}/v3/metasearch/hotels/searches`,
        user,
        search: {
          siteCode: currentSite.countryCode,
          locale,
          currencyCode: currency.code,
          checkIn: checkInDate!,
          checkOut: checkOutDate!,
          rooms: guestRooms,
          ...((searchType === 'city' || searchType === 'district') &&
            (!radiusLatParam || !radiusLongParam) && {
              cityCode: cityCode!.toUpperCase(),
            }),
          ...(searchType === 'district' &&
            (!radiusLatParam || !radiusLongParam) && {
              districtId: districtId!,
            }),
          ...(searchType === 'region' &&
            (!radiusLatParam || !radiusLongParam) && {
              regionId: regionId!,
            }),
          ...(radiusLatParam &&
            radiusLongParam && {
              longitude: parseFloat(radiusLongParam),
              latitude: parseFloat(radiusLatParam),
              radius: radiusParam ? +radiusParam : 2,
            }),
        },
        clientId: wegoAnalyticsClientId,
        sessionId: wegoAnalyticsClientSessionId,
        selectedHotelIds,
        extraSearchBodyParams,
        extraBodyParams,
      };
      const isGoogleHotel =
        !!wegoHotelIdSearchParam &&
        !!providerSearchParam &&
        window.atob(providerSearchParam) === 'agoda.com' &&
        tsCodeSearchParam === '2f2fc';
      const hotelSearchObservable$ = hotelSearchObservable({ ...requestParams, isGoogleHotel });

      dispatch(resetHotelMetaSearch());

      setIsSearchExpired(false);
      setIsLoading(true);
      setIsLoadingMapDelayed(true);
      setIsWebEngageTracked(false);
      setIsGenzoTracked(false);
      setIsGenzoVisitsTracked(false);
      setIsGtmTracked(false);
      setHotelMetaSearchHasError(false);

      const subscription = hotelSearchObservable$.subscribe(
        hotelSearchResultObserver(
          dispatch,
          priceOptions,
          () => {
            setIsLoading(false);
            setIsLoadingMapDelayed(false);
          },
          (err) => {
            console.log('! [HotelSearchResult] ERROR', err);
            if (err) {
              const errorStatus = err.toString().split(':')[1].trim();
              console.log('! [HotelSearchResult:errorStatus]', err.toString().split(':')[1].trim());

              // This is when the user is on safari and have popup block settings enabled and press Login
              // There's a chance that this observable will throw an error probably because of component unmounting,
              // so this condition ignores that. React errors will throw an error string
              // compare to API errors that throws http status codes.
              if (isNaN(Number(errorStatus))) {
                return;
              }

              setIsLoading(false);
              setIsLoadingMapDelayed(false);
              setHotelMetaSearchHasError(true);
              setHotelMetaSearchError({
                status: errorStatus,
                message: err,
              });
            }
          },
        ),
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [
    params.cityCode,
    params.districtId,
    params.regionId,
    params.checkInDate,
    params.checkOutDate,
    guestsSearchParam,
    wegoHotelIdSearchParam,
    providerSearchParam,
    tsCodeSearchParam,
    user,
    locale,
    triggerSearchCounter,
    selectedHotelIds,
  ]);

  // Disable body scroll if filters drawer is open
  useEffect(() => {
    if (isFiltersDrawerOpen) {
      setIsBodyScrollable(false);
    } else {
      setIsBodyScrollable(true);
    }
  }, [isFiltersDrawerOpen]);

  // Recompute row height when sortedAndFilteredHotelViewModels length changes
  useEffect(() => {
    if (!!listRef && !!listRef.current && sortedAndFilteredHotelViewModels.length > 0) {
      listRef.current.recomputeRowHeights(0);
    }
  }, [listRef, sortedAndFilteredHotelViewModels]);

  // Recompute row height when topAdHeight, middleAdHeight and bottomAdHeight changes
  useEffect(() => {
    if (!!listRef && !!listRef.current) {
      listRef.current.recomputeRowHeights(0);
    }
  }, [listRef, topAdHeight, middleAdHeight, bottomAdHeight, travelerTypeTop]);

  // Expire search dialog timeout
  useEffect(() => {
    if (!!hotelMetaSearch.search?.createdAt) {
      const hotelMetaSearchExpireByMilliseconds =
        new Date(hotelMetaSearch.search.createdAt).getTime() + 1800 * 1000; // 1,800,000 milliseconds = 30mins
      const nowMilliseconds = Date.now();

      if (hotelMetaSearchExpireByMilliseconds > nowMilliseconds) {
        const refreshSearchTimeout = setTimeout((): void => {
          setIsSearchExpired(true);
        }, hotelMetaSearchExpireByMilliseconds - nowMilliseconds);

        return () => {
          clearTimeout(refreshSearchTimeout);
        };
      }
    }
  }, [hotelMetaSearch.search?.createdAt]);

  useEffect(() => {
    if (hotelMetaSearch.hotelViewModels.length && isLoadingMapDelayed) {
      const timer = setTimeout(() => {
        setIsLoadingMapDelayed(false);
      }, 0);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [hotelMetaSearch.hotelViewModels.length, isLoadingMapDelayed]);

  useEffect(() => {
    if (isOpenSearchbox) {
      searchboxRef.current?.focus();
    }
  }, [isOpenSearchbox]);

  return (
    <>
      {/* Section: Error when hotelMetaSearchHasError */}
      {/* Message: Sorry, looks like something went wrong.. hotelMetaSearchError */}
      {hotelMetaSearchHasError && !isLoading && isOnline && isDatesValid ? (
        <AlertMessage
          type={MessageTypes.SOMETHING_WRONG}
          className={styles.alertWrapper}
          title={errorAlertContents.title as string}
          message={errorAlertContents.message as string}
          buttonLabel={errorAlertContents.buttonLabel as string}
          onClick={() => navigate(0)}
        />
      ) : null}

      {/* Section: Connection Lost */}
      {!isOnline && !isLoading ? (
        <AlertMessage
          type={MessageTypes.NO_INTERNET}
          className={styles.alertWrapper}
          title={translations.lost_connection_header as string}
          message={translations.lost_connection_message as string}
          buttonLabel={translations.click_to_retry as string}
          onClick={() => navigate(0)}
        />
      ) : null}

      {/* Section: Invalid Date */}
      {!isDatesValid ? (
        <AlertMessage
          type={MessageTypes.INVALID_DATE}
          className={styles.alertWrapper}
          title={translations.err_invalid_date as string}
          message={
            new Date(params.checkInDate!) < new Date(params.checkOutDate!)
              ? (translations.err_invalid_date_message as string)
              : (translations.check_in_check_out_same_message as string)
          }
          buttonLabel=''
          onClick={() => {}}
        />
      ) : null}

      {!hotelMetaSearchHasError && isOnline && isDatesValid ? (
        <div
          className={clsx(styles.container, isMapVisible && styles.mapViewV2, isRtl && styles.rtl)}
        >
          <AdsBlockerBait />
          <div
            className={clsx(styles.overlay, styles.filter, isFiltersDrawerOpen && styles.open)}
            onClick={() => setIsFiltersDrawerOpen(false)}
          ></div>

          <div
            className={clsx(styles.overlay, styles.searchExpired, isSearchExpired && styles.open)}
            onClick={() => handleRefreshSearch(triggerSearchCounter)}
          ></div>

          <div
            className={clsx(
              styles.loadingMap,
              isMapVisible &&
                !!googleMap &&
                isLoading &&
                !hotelMetaSearch.hotelViewModels.length &&
                styles.open,
            )}
          ></div>

          {isSearchExpired ? (
            <RefreshPopup
              genzoTrackPageViewBaseType='hotels_search_results'
              product={ProductTypes.HOTELS}
              localeParam={localeParam}
              onRefresh={() => handleRefreshSearch(triggerSearchCounter)}
              onClose={() => setIsSearchExpired(false)}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            />
          ) : null}

          <div
            className={clsx(
              styles.filterAndResultAndAdsContainer,
              isMapVisible && !isHalfScreenMap && styles.widthUnset,
              isMapFullscreen && styles.hide,
            )}
          >
            <div className={clsx(styles.filterContainer, isFiltersDrawerOpen && styles.open)}>
              {/* FiltersDrawerToggleButton(Double arrow button): Map view*/}
              <button
                data-testid='filters-drawer-toggle-button'
                type='button'
                className={styles.filtersDrawerToggleButton}
                onClick={() => setIsFiltersDrawerOpen(!isFiltersDrawerOpen)}
              >
                <DoubleArrowRightIcon />
              </button>

              <div className={styles.filters} data-testid='filters-section'>
                {/* Header filter: Map view */}
                <div className={styles.header}>
                  {translations.filters}

                  <CloseIcon
                    data-testid='filters-drawer-close-button'
                    className={styles.icon}
                    onClick={() => setIsFiltersDrawerOpen(false)}
                  />
                </div>

                <button
                  data-testid='map-view-toggle-button'
                  type='button'
                  className={clsx(
                    styles.mapViewButton,
                    isHalfScreenMap && isMapVisible && styles.hidden,
                  )}
                  onClick={() => {
                    handleMapViewToggle(!isMapVisible);
                  }}
                >
                  {!isMapVisible ? (
                    <>
                      <MapIcon className={styles.icon} />
                      {translations.map_view}
                    </>
                  ) : (
                    <>
                      <ListViewIcon className={styles.icon} />
                      {translations.list_view}
                    </>
                  )}
                </button>

                <HotelFilter
                  className={styles.hotelFilter}
                  filteredHotelsCount={
                    sortedAndFilteredHotelViewModels.length +
                    selectedHotelViewModels.length +
                    googleHotelViewModels.length
                  }
                  onClearFilter={handleOnClearFilter}
                  onPopularFilterClick={handleOnPopularFilterClick}
                  todayDealsCount={todayDealsCount}
                  isTodayDeals={!!isTodayDealsSearchParam}
                  onIsTodayDealsToggle={handleIsTodayDealsToggle}
                  minUsdPrice={minUsdPrice}
                  maxUsdPrice={maxUsdPrice}
                  selectedUsdPrices={selectedUsdPrices}
                  onSelectedUsdPricesChanged={handleSelectedUsdPricesChanged}
                  onIsIncludingTaxChanged={handleIsIncludingTaxChanged}
                  onShowTotalPriceChanged={handleShowTotalPriceChanged}
                  reviewScoresRange={reviewScoresRange}
                  selectedReviewScores={selectedReviewScores}
                  onSelectedReviewScoresChanged={handleSelectedReviewScoresChanged}
                  selectedAmenityIds={selectedAmenityIds}
                  onAmenityToggle={handleAmenityFilterToggle}
                  selectedBookingOptions={selectedBookingOptions}
                  onBookingOptionToggle={handleBookingOptionFilterToggle}
                  selectedBrandIds={selectedBrandIds}
                  onBrandToggle={handleBrandFilterToggle}
                  selectedChainIds={selectedChainIds}
                  onChainToggle={handleChainFilterToggle}
                  selectedDistrictIds={selectedDistrictIds}
                  onDistrictToggle={handleDistrictFilterToggle}
                  selectedPropertyTypeIds={selectedPropertyTypeIds}
                  onPropertyTypeToggle={handlePropertyTypeFilterToggle}
                  selectedRateAmenityIds={selectedRateAmenityIds}
                  onRateAmenityToggle={handleRateAmenityFilterToggle}
                  selectedReviewerGroups={selectedReviewerGroups}
                  onReviewerGroupToggle={handleReviewerGroupFilterToggle}
                  selectedStarIds={selectedStarIds}
                  onStarToggle={handleStarFilterToggle}
                  selectedRateProviderCodes={selectedRateProviderCodes}
                  onRateProviderToggle={handleRateProviderToggle}
                />
              </div>
            </div>

            <div className={clsx(styles.result, !isHalfScreenMap && isMapVisible && styles.hidden)}>
              {hasEgyptCentralBankNotice ? (
                <div className={styles.egyptCentralBankNotice}>
                  <span>
                    <MegaPhoneIcon height={32} width={32} />
                  </span>
                  {translations.eg_central_bank_fee_text}
                </div>
              ) : null}

              {isShowTravelerBox ? (
                <div
                  style={{
                    top: `${travelerTypeTop}px`,
                    ...((!isOpenTravelerTypeBox || sortedAndFilteredHotelViewModels.length < 6) && {
                      display: 'none',
                    }),
                  }}
                  className={styles.travelerBox}
                >
                  <TravelerTypeBox
                    setIsOpen={setIsOpenTravelerTypeBox}
                    pageViewId={pageViewId}
                    wegoAnalyticsClientId={wegoAnalyticsClientId}
                    wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
                  />
                </div>
              ) : null}

              <div
                className={clsx(styles.sortFilterSearchContainer, isMapVisible && styles.sticky)}
              >
                <div className={clsx(styles.filterAndSort, isOpenSearchbox && styles.hide)}>
                  {(isHalfScreenMap && isMapVisible) || (!isMapVisible && !isHalfScreenMap) ? (
                    <div
                      data-testid='filter-button'
                      className={clsx(styles.filterButton, hasFilteredApplied && styles.indicators)}
                      onClick={() => setIsFiltersDrawerOpen(true)}
                    >
                      <AdjustmentIcon className={styles.icon} />
                    </div>
                  ) : null}

                  <HotelSort
                    onSortDropdownOpen={handleOnSortDropdownOpen}
                    onSortTypeChanged={handleOnSortTypeChanged}
                    selectedSortOrder={sortOrder}
                    selectedSortType={sortType}
                  />

                  <div
                    data-testid='searchbox-button'
                    className={styles.filterButton}
                    onClick={() => setIsOpenSearchbox((prev) => !prev)}
                  >
                    <SearchIcon className={styles.icon} />
                  </div>
                </div>

                <div
                  data-testid='searchbox-button-onblur'
                  className={styles.searchBar}
                  data-open={isOpenSearchbox}
                  onBlur={() => {
                    if (!nameSearchParam) setIsOpenSearchbox((prev) => !prev);
                  }}
                >
                  <SearchIcon className={styles.icon} />
                  <input
                    autoComplete='off'
                    inputMode='text'
                    onChange={handleHotelNameSearchQueryChanged}
                    placeholder={translations.search_by_name as string}
                    spellCheck={false}
                    type='text'
                    value={nameSearchParam || ''}
                    ref={searchboxRef}
                  />
                  {nameSearchParam ? (
                    <div className={styles.closeIcon} onClick={handleClearHotelSearchName}>
                      <CloseFilledIcon />
                    </div>
                  ) : null}
                </div>
              </div>

              <HotelLoginBanner
                wegoAnalyticsClientId={wegoAnalyticsClientId}
                wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
                hotelCityName={hotelMetaSearch?.search?.city?.name}
              />

              {/* Skeleton */}
              <HotelCardSkeleton
                isLoading={isLoading}
                hotelViewModelsLength={sortedAndFilteredHotelViewModels?.length || 0}
              />

              {selectedHotelViewModels.length > 0 ? (
                <>
                  <div className={styles.selectedHotelLabel}>
                    {translateText(
                      translations.lbl_property_selected,
                      locale,
                      selectedHotelViewModels.length,
                    )}{' '}
                    :
                  </div>

                  <div className={styles.selectedHotels}>
                    {selectedHotelViewModels.map((hotelViewModel, index) => (
                      <HotelCard
                        hasTabbyGateway={hasTabbyGateway}
                        hotelViewModel={hotelViewModel}
                        key={index}
                        localeParam={localeParam}
                        onHotelCardClick={(selectedRate, hotelViewModel, typeOfClick) => {
                          handleOnHotelCardClick(selectedRate, hotelViewModel, index, typeOfClick);
                        }}
                        searchId={hotelMetaSearch.search!.id}
                        showSponsoredAd={false}
                        wegoAnalyticsClientId={wegoAnalyticsClientId}
                        wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
                        isShowReview={listRenderId[hotelViewModel.hotelId]}
                        setListRenderId={setListRenderId}
                        isSelectedHotel
                        isLoadingRates={isLoading}
                      />
                    ))}
                  </div>

                  <div className={styles.similarHotelLabel}>{translations.might_like_hotels}</div>
                </>
              ) : null}

              {googleHotelViewModels.length > 0 ? (
                <>
                  {googleHotelViewModels.map((hotelViewModel, index) => (
                    <HotelCard
                      hasTabbyGateway={hasTabbyGateway}
                      hotelViewModel={hotelViewModel}
                      isHotelFromGoogle={!!wegoHotelIdSearchParam && !!providerSearchParam}
                      key={index}
                      localeParam={localeParam}
                      onHotelCardClick={(selectedRate, hotelViewModel, typeOfClick) => {
                        handleOnHotelCardClick(selectedRate, hotelViewModel, index, typeOfClick);
                      }}
                      searchId={hotelMetaSearch.search!.id}
                      showSponsoredAd={false}
                      wegoAnalyticsClientId={wegoAnalyticsClientId}
                      wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
                      isShowReview={listRenderId[hotelViewModel.hotelId]}
                      setListRenderId={setListRenderId}
                    />
                  ))}
                  <div className={styles.morePropertiesLabel}>
                    {translateText(translations.more_properties_label, locale)}
                  </div>
                </>
              ) : null}

              {sortedAndFilteredHotelViewModels.length > 0 ? (
                <WindowScroller scrollElement={window}>
                  {({
                    height,
                    isScrolling,
                    registerChild,
                    onChildScroll,
                    scrollTop,
                  }: WindowScrollerChildProps) => (
                    <AutoSizer ref={null} disableHeight>
                      {({ width }: Size) => (
                        <div ref={registerChild}>
                          <List
                            ref={listRef}
                            autoHeight
                            height={height}
                            isScrolling={isScrolling}
                            onScroll={onChildScroll}
                            overscanRowCount={5}
                            rowCount={rowCount}
                            rowHeight={getRowHeight}
                            rowRenderer={rowRenderer}
                            scrollTop={scrollTop}
                            tabIndex={null}
                            width={width}
                          />
                        </div>
                      )}
                    </AutoSizer>
                  )}
                </WindowScroller>
              ) : null}

              {/* Section: NoResult when loading is done and sortedAndFilteredHotelViewModels length === 0 */}
              {/* Message: Sorry, there are no results matching your filters. Try removing one. */}
              {!hotelMetaSearchHasError &&
              !isLoading &&
              hotelMetaSearch.hotelViewModels.length > 0 &&
              sortedAndFilteredHotelViewModels.length === 0 ? (
                <AlertMessage
                  type={MessageTypes.NO_RESULTS}
                  className={styles.noResults}
                  title={translations.no_results_header_v2 as string}
                  message={translations.no_results as string}
                  buttonLabel={translations.reset_filters as string}
                  onClick={handleOnClearFilter}
                />
              ) : null}

              {/* Section: NoResult when loading is done and hotelMetaSearch.hotelViewModels length === 0 */}
              {/* Message: Sorry, there are no results for your search. Please try a new set of dates. */}
              {!hotelMetaSearchHasError &&
              !isLoading &&
              hotelMetaSearch.hotelViewModels.length === 0 ? (
                <AlertMessage
                  type={MessageTypes.NO_RESULTS}
                  className={styles.noResults}
                  title={translations.no_results_header_v2 as string}
                  message={translations.sorry_no_results as string}
                  buttonLabel={translations.go_back as string}
                  onClick={handleHotelSearchResultOnBack}
                />
              ) : null}

              {/* Section: Hotel In Town ads */}
              <div
                id='new_hotel_in_town'
                className={clsx(
                  styles.dWebAdsContainer,
                  sortedAndFilteredHotelViewModels.length < 3 && styles.hide,
                )}
                style={{ top: `${topAdTop}px` }}
              />

              {/* Section: Native ads */}
              <div
                id='native_top_ad'
                className={clsx(
                  styles.dWebAdsContainer,
                  (sortedAndFilteredHotelViewModels.length < 3 || !!newHotelInTownAdHeight) &&
                    styles.hide,
                )}
                style={{ top: `${topAdTop}px` }}
              />
              <div
                id='native_middle_ad'
                className={clsx(
                  styles.dWebAdsContainer,
                  (sortedAndFilteredHotelViewModels.length < 6 ||
                    (!!sortedAndFilteredHotelViewModels[6] &&
                      sortedAndFilteredHotelViewModels[6].sponsoredAd) ||
                    !!nativeMiddleAdHeight) &&
                    styles.hide,
                )}
                style={{ top: `${middleAdTop}px` }}
              />
              <div
                id='native_bottom_ad'
                className={clsx(
                  styles.dWebAdsContainer,
                  sortedAndFilteredHotelViewModels.length < 8 && styles.hide,
                )}
                style={{ top: `${bottomAdTop}px` }}
              />

              {/* Section: dWeb Ads (banner ads) */}
              <div
                id='top_leaderboard'
                className={clsx(
                  styles.dWebAdsContainer,
                  (sortedAndFilteredHotelViewModels.length < 3 ||
                    !!nativeTopAdHeight ||
                    !!newHotelInTownAdHeight) &&
                    styles.hide,
                )}
                style={topAdTop > 0 ? { top: `${topAdTop}px` } : {}}
              ></div>
              <div
                id='middle_leaderboard'
                className={clsx(
                  styles.dWebAdsContainer,
                  (sortedAndFilteredHotelViewModels.length < 6 ||
                    (!!sortedAndFilteredHotelViewModels[6] &&
                      sortedAndFilteredHotelViewModels[6].sponsoredAd) ||
                    !!nativeMiddleAdHeight) &&
                    styles.hide,
                )}
                style={middleAdTop > 0 ? { top: `${middleAdTop}px` } : {}}
              ></div>
              <div
                id='bottom_leaderboard'
                className={clsx(
                  styles.dWebAdsContainer,
                  (sortedAndFilteredHotelViewModels.length < 8 || !!nativeBottomAdHeight) &&
                    styles.hide,
                )}
                style={bottomAdTop > 0 ? { top: `${bottomAdTop}px` } : {}}
              ></div>
            </div>

            <div
              className={clsx(
                styles.adsContainer,
                hasMrecOrStickyAds && styles.show,
                !isMapClosed && styles.hide,
              )}
            >
              <div id='mrec_1' className={styles.mrec1}></div>
              <div id='mrec_2'></div>
              <div className={styles.stickyAdsContainer}>
                <div id='sticky_top'></div>
                {showStickyBottom ? <div id='sticky_bottom'></div> : null}
              </div>
            </div>

            <div id='skyscraper' className={clsx(styles.skyscraperAdsContainer)}></div>
          </div>

          <div
            className={clsx(
              styles.mapWrapper,
              !isMapVisible && styles.hide,
              isLoadingSideMap && styles.isLoadingMapWrapper,
            )}
          >
            <div className={styles.newFiltersButton} onClick={() => setIsFiltersDrawerOpen(true)}>
              <AdjustmentIcon className={styles.icon} />
              <div className={clsx(hasFilteredApplied && styles.indicators)}>
                {translations.filters}
              </div>
            </div>

            {isLoadingSideMap ? (
              <div data-testid='loading-dot' className={styles.loadingDots}>
                {[...Array(4).keys()].map((i) => (
                  <div key={i} className={styles.dot}></div>
                ))}
              </div>
            ) : null}

            {!isLoadingMapDelayed ? (
              <HotelMap
                setLoading={setIsLoading}
                className={styles.mapV2}
                hotelViewModels={sortedAndFilteredHotelViewModels}
                isLoadingCitySearch={isLoadingMapDelayed}
                isMapFullscreen={isMapFullscreen}
                onApplyMap={(googleMap) => setGoogleMap(googleMap)}
                onHotelCardMapClick={handleOnHotelCardMapClick}
                onCloseMap={() => {
                  setMapFullscreen(false);
                  setIsMapClosed(true);
                  handleMapViewToggle(false);
                }}
                setMapFullscreen={setMapFullscreen}
                wegoAnalyticsClientId={wegoAnalyticsClientId}
                wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default HotelSearchResult;
